import React, { useState, useMemo, useEffect } from 'react';
import { AmountInput } from '../common/AmountInput';
import {
  Inbox,
  LayoutDashboard,
  Smartphone,
  Monitor,
  Users,
  FolderLock,
  PhoneCall,
  Calendar,
  MapPin,
  DollarSign,
  Camera,
  Mic,
  FileText,
  Database,
  BarChart3,
  Percent,
  MessageSquare,
  Table,
  HardDrive,
  Settings,
  Search,
  Plus,
  Filter,
  Download,
  CheckCircle2,
  AlertTriangle,
  ArrowUpRight,
  TrendingUp,
  Clock,
  ChevronRight,
  Shield,
  Eye,
  RefreshCw,
  Trash2,
  Edit,
  Send,
  Sparkles,
  FileSpreadsheet,
  Upload,
  Layers,
  Zap,
  X,
  LogOut,
  Menu,
  Network,
  Building2,
  RotateCcw,
  Archive,
  Star,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { Account, User, UserRole, PaymentMode, FollowUp, PTPRecord, RecoveryRecord, FieldVisit } from '../../types';
import { formatINR } from '../../utils/watermark';
import { ScaleSupportLogo } from '../common/ScaleSupportLogo';
import { MessageCentre } from '../common/MessageCentre';
import { ExcelAllocationModal } from './ExcelAllocationModal';
import { downloadExactExcelTemplate, exportAccountsToExactExcel } from '../../utils/excelAccountImporter';
import { AccountTimelineDrawer } from '../common/AccountTimelineDrawer';
import { WatermarkCameraModal } from '../common/WatermarkCameraModal';
import { VoiceNoteRecorderModal } from '../common/VoiceNoteRecorderModal';
import { firebaseFirestoreService } from '../../services/firebaseFirestoreService';
import { CustomerConsolidatedDetailModal } from '../common/CustomerConsolidatedDetailModal';
import CommissionRatesSettings from './CommissionRatesSettings';
import BranchMergeSettings from './BranchMergeSettings';
import AgentLoginCounts from './AgentLoginCounts';
import EnquiriesPanel from './EnquiriesPanel';
import { PTPDigestModal } from '../common/PTPDigestModal';
import { AgentWebPortal } from '../agent/AgentWebPortal';
import { CommissionManagementSection } from './CommissionManagementSection';
import { DynamicCommissionSection } from './DynamicCommissionSection';
import { HierarchyManagementSection } from './HierarchyManagementSection';
import { StarRating } from '../common/StarRating';
import { CloseAccountModal } from '../common/CloseAccountModal';
import {
  LogFollowUpModal,
  CreatePTPModal,
  RecordRecoveryModal,
  FieldVisitModal,
  UploadDocumentModal,
  WhatsAppTriggerModal,
  AIRecoveryDrawer,
  AddAgentNoteModal,
} from '../common/ActionModals';
import { OTSSettingsModal } from '../common/OTSSettingsModal';
import { BulkDeleteAccountsModal } from '../common/BulkDeleteAccountsModal';
import { BackendStorageManagerModal } from './BackendStorageManagerModal';
import { HierarchyPicker } from '../common/HierarchyPicker';
import { AccountsScopeBar } from '../common/AccountsScopeBar';
import { AccountsFilterBar } from '../common/AccountsFilterBar';
import { AccountCategoryModal } from './AccountCategoryModal';
import { LogScopeGate } from '../common/LogScopeGate';
import { latestAgentNote, productOf } from '../../utils/collections';
import { PTPDueTodayBanner } from '../common/PTPDueTodayBanner';
import {
  AccountFilters,
  ACCOUNTS_PER_PAGE,
  applyAccountFilters,
  emptyAccountFilters,
  productOptionsOf,
  accountCategoryOptionsOf,
  pageOf,
  todayIso,
} from '../../utils/accountFilters';
import { AgentPlanFields, AgentPlan, emptyAgentPlan, isoDay } from '../common/AgentPlanFields';
import {
  branchesOfBankZone,
  findBranchByName,
  findZoneByName,
  planStatusOf,
  requirementsFor,
  validateUserHierarchy,
  zonesOfBank,
} from '../../utils/hierarchy';
import { userInBranch } from '../../utils/branchMatch';

import { groupAccountsByCustomer } from '../../utils/linkedAccounts';
import { displayDate } from '../../utils/dates';
export const WebPortal: React.FC = () => {
  const {
    currentUser,
    users,
    accounts,
    followups,
    ptps,
    recoveries,
    visits,
    photos,
    voiceNotes,
    documents,
    commissions,
    commissionSettings,
    updateCommissionRate,
    allocateAccount,
    allocateBulkAccounts,
    toggleUserActive,
    addUser,
    deleteGeoTaggedPhoto,
    updatePhotoNote,
    userBanks,
    activeBank,
    setActiveBank,
    updateUser,
    changeUserPassword,
    deleteUser,
    deleteAccount,
    updateCustomerStarRating,
    closeAccount,
    reverseRecovery,
    deleteAllAccountsFromStorage,
    deleteBulkAccounts,
    exportAllBackendData,
    logout,
    branches,
    zones,
    areas,
    notifications,
    getExpiredPTPPreview,
    banks,
    recoveryDepartments,
    deletePTP,
    scopedAccounts,
    scopedRecoveries,
    scopedPtps,
    scopedVisits,
    scopedCommissions,
    scopedUsers,
    accountsScope,
    accountsLoading,
    pendingScopeReload,
    resumeLastScope,
    countAccountsFor,
    changeUserLoginId,
    unreadMessageCount,
    firestoreSpeedStats,
  } = useSRMS();

  /**
   * ONE ENTRY PER PLACE, whatever spelling it arrived in.
   *
   * These lists were built with a plain Set, which treats "GARKHEDA" and
   * "Garkheda" as two different branches -- so the same branch appeared twice
   * in every dropdown, and picking one of them showed only the accounts filed
   * under that exact spelling.
   *
   * Names are now folded on capitals and spacing. The spelling that survives
   * is the one from the branch or zone RECORD when there is one, because that
   * is the spelling an administrator chose deliberately; otherwise it is the
   * first that came in from the data.
   */
  const foldNames = (preferred: Array<string | undefined>, rest: Array<string | undefined>) => {
    const key = (v?: string) => (v || '').trim().replace(/\s+/g, ' ').toLowerCase();
    const chosen = new Map<string, string>();
    preferred.forEach((n) => {
      const k = key(n);
      if (k && !chosen.has(k)) chosen.set(k, (n || '').trim());
    });
    rest.forEach((n) => {
      const k = key(n);
      if (k && !chosen.has(k)) chosen.set(k, (n || '').trim());
    });
    return Array.from(chosen.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  };

  // Dynamic unique branches & zones list strictly derived from uploaded accounts, system branches, and users
  const allUniqueBranches = useMemo(
    () =>
      foldNames(
        branches.map((b) => b.name),
        [...accounts.map((a) => a.branch), ...users.map((u) => u.branch)]
      ),
    [branches, accounts, users]
  );

  const allUniqueZones = useMemo(
    () =>
      foldNames(
        zones.map((z) => z.name),
        [...accounts.map((a) => a.zone), ...users.map((u) => u.zone)]
      ),
    [zones, accounts, users]
  );

  /**
   * The products to offer in the shortlist, taken from the accounts that are
   * already loaded. `LoanType` -- the seven-value list this filter would
   * otherwise be built from -- is a category the importer derives, not what
   * the bank calls its products, so a filter made from it listed products no
   * account holds and omitted every product they do.
   */
  const productFilterOptions = useMemo(
    () => productOptionsOf(accounts),
    [accounts]
  );
  const accountCategoryFilterOptions = useMemo(
    () => accountCategoryOptionsOf(accounts),
    [accounts]
  );

  const allUniqueZonalOffices = useMemo(
    () =>
      foldNames(
        zones.map((z) => z.name),
        [
          ...accounts.map((a) => a.zonalOffice || a.zone),
          ...users.map((u) => u.zonalOffice || u.zone),
        ]
      ),
    [zones, accounts, users]
  );

  const allUniqueBanks = useMemo(
    () =>
      foldNames(
        banks.map((b) => b.name),
        accounts.map((a) => (a as any).bankName || (a as any).bank)
      ),
    [banks, accounts]
  );

  // Active Web Section
  const [activeSection, setActiveSection] = useState<string>('dashboard');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState<boolean>(false);

  // Month Strings & Defaults
  const currentMonthStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  // Dashboard Hierarchy & Multi-dimension Filters
  const [dashUserFilter, setDashUserFilter] = useState<string>('ALL');
  const [dashBankFilter, setDashBankFilter] = useState<string>('ALL');
  const [dashZoneFilter, setDashZoneFilter] = useState<string>('ALL');
  const [dashBranchFilter, setDashBranchFilter] = useState<string>('ALL');
  const [dashMonthFilter, setDashMonthFilter] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  // Recovery Section Month Filter (default current month)
  const [recoveryMonthFilter, setRecoveryMonthFilter] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  // OTS Settings Modal State (Admin only)
  const [isOTSSettingsOpen, setIsOTSSettingsOpen] = useState<boolean>(false);
  const [isBackendStorageModalOpen, setIsBackendStorageModalOpen] = useState<boolean>(false);

  // Search & Filters
  //
  // Two values, on purpose. `searchInput` is what is being typed;
  // `searchTerm` is what the list is actually filtered by, and it only
  // changes when Search is pressed or Enter hit. Filtering on every keystroke
  // is fine over a few hundred accounts and wasteful over tens of thousands,
  // and it also meant a half-typed name briefly showed an empty list.
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const applySearch = (value?: string) => {
    setSearchTerm((value !== undefined ? value : searchInput).trim());
  };

  // Emptying the box brings the full list straight back, with no extra click.
  const onSearchInputChange = (value: string) => {
    setSearchInput(value);
    if (value.trim() === '') setSearchTerm('');
  };

  /**
   * The header search box, made to actually do something.
   *
   * It writes into the same filter object the Loan Accounts bar uses -- there
   * is one search in the app now, not two -- and moves you to that screen so
   * you can see the result. Nothing is read from the database by this: it
   * narrows the accounts already loaded for the scope you chose.
   */
  const runHeaderSearch = () => {
    const term = searchInput.trim();
    applySearch(term);
    setAcctFilters((prev) => ({ ...prev, text: term }));
    setAcctPage(1);
    if (term) setActiveSection('accounts');
  };
  // One object instead of five separate filter variables that each screen
  // read in a different order. Sorting, the amount range and the rating
  // shortlist live here too, which is what let the separate Shortlist menu go.
  const [acctFilters, setAcctFilters] = useState<AccountFilters>(emptyAccountFilters);

  /**
   * Does this person genuinely work across banks, zones and regions?
   *
   * Only an administrator does. A recovery department user has one bank and
   * one zone; a branch manager has one branch. Offering them a "Bank Wise"
   * dropdown listing three banks does not widen what they can see -- their
   * account scope is fixed elsewhere -- it just tells them something untrue
   * about their own remit and invites a selection that will not hold.
   */
  const spansWholeOrganisation =
    currentUser?.role === 'admin' ||
    (currentUser?.role as string) === 'super_admin' ||
    currentUser?.role === 'management';

  /**
   * WHO MAY SEE THE ADMINISTRATION SCREENS.
   *
   * THE HOLE THIS CLOSES. The sidebar was not gated by role at all. A branch
   * manager, a coordinator and a recovery department user were all handed the
   * complete administrator's menu -- System Settings included, and inside it
   * "Bulk Delete by Agent / Branch / All", which removes accounts in bulk.
   * They could also open Users & Agents, where logins are created and
   * deactivated, and the Commission Engine, where the rates that decide what
   * every agent is paid are set.
   *
   * Their ACCOUNT scope was always correct -- a branch manager only ever read
   * their own branch. It was the administration screens that were open to
   * everybody, and those are not scoped by branch: a bulk delete is a bulk
   * delete.
   *
   * Three screens are now administrators only. Everything operational --
   * accounts, allocation, follow-ups, PTPs, visits, recovery logs, photos,
   * voice notes, hierarchy, reports, messages -- is untouched for every role.
   */
  const isAdminActor =
    currentUser?.role === 'admin' || (currentUser?.role as string) === 'super_admin';

  const ADMIN_ONLY_SECTIONS = ['settings', 'users', 'commission', 'commissionRuleOption'];

  /**
   * A section reached some other way -- a stale tab, a button elsewhere, a
   * restored session -- is turned away here as well. Hiding a menu entry is
   * not access control on its own.
   */
  useEffect(() => {
    if (!isAdminActor && ADMIN_ONLY_SECTIONS.includes(activeSection)) {
      setActiveSection('dashboard');
    }
  }, [isAdminActor, activeSection]);

  /**
   * The agent whose logs are being looked at, or null while none is chosen.
   *
   * Recovery Logs, Follow-ups, PTP Commitments, Field Visits, Geo-tagged
   * Photos and Voice Notes used to open by listing every record in the
   * business. Each now shows a count per agent and loads nothing until one is
   * picked -- which is the point about reads, and also the difference between
   * a screen that opens and a screen that hangs.
   */
  const [logAgent, setLogAgent] = useState<User | null>(null);

  const logAgentKeys = useMemo(() => {
    if (!logAgent) return [] as string[];
    return [logAgent.agentId, logAgent.id, logAgent.name]
      .filter((x): x is string => !!x)
      .map((x) => x.trim().toLowerCase());
  }, [logAgent]);

  /**
   * An agent is matched by id OR by name, because older records carry only the
   * name. Matching on id alone silently hid an agent's own older work from
   * them.
   */
  const belongsToLogAgent = React.useCallback(
    (rec: any): boolean => {
      if (logAgentKeys.length === 0) return false;
      const id = String(rec?.agentId || '').trim().toLowerCase();
      const name = String(rec?.agentName || '').trim().toLowerCase();
      return (!!id && logAgentKeys.includes(id)) || (!!name && logAgentKeys.includes(name));
    },
    [logAgentKeys]
  );

  // Empty until an agent is chosen -- deliberately, not as a failure.
  const gatedFollowups = useMemo(
    () => followups.filter(belongsToLogAgent),
    [followups, belongsToLogAgent]
  );
  const gatedVisits = useMemo(
    () => visits.filter(belongsToLogAgent),
    [visits, belongsToLogAgent]
  );
  const gatedVoiceNotes = useMemo(
    () => voiceNotes.filter(belongsToLogAgent),
    [voiceNotes, belongsToLogAgent]
  );
  const gatedPhotos = useMemo(
    () => photos.filter(belongsToLogAgent),
    [photos, belongsToLogAgent]
  );

  // 25 rows at a time. Rendering tens of thousands of rows is what made this
  // screen crawl, quite apart from what it cost to read them.
  const [acctPage, setAcctPage] = useState(1);
  // Firestore tables in the Collections view show 15 rows, then "Show more".
  const [collShown, setCollShown] = useState(15);

  const [filterZone, setFilterZone] = useState<string>('ALL');
  const [filterBranch, setFilterBranch] = useState<string>('ALL');
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [filterStarRating, setFilterStarRating] = useState<string>('ALL');
  const [ptpFilterStatus, setPtpFilterStatus] = useState<string>('ALL');
  const [selectedAgentForAllocation, setSelectedAgentForAllocation] = useState<string>('');
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);

  // Modals state
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [isWatermarkOpen, setIsWatermarkOpen] = useState(false);
  const [isVoiceOpen, setIsVoiceOpen] = useState(false);
  const [isFollowUpOpen, setIsFollowUpOpen] = useState(false);
  const [isPTPOpen, setIsPTPOpen] = useState(false);
  const [isRecoveryOpen, setIsRecoveryOpen] = useState(false);
  const [isVisitOpen, setIsVisitOpen] = useState(false);
  const [isDocOpen, setIsDocOpen] = useState(false);
  const [isWhatsAppOpen, setIsWhatsAppOpen] = useState(false);
  const [isAIOpen, setIsAIOpen] = useState(false);
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [isSingleCustomerModalOpen, setIsSingleCustomerModalOpen] = useState(false);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isPTPDigestOpen, setIsPTPDigestOpen] = useState(false);

  // New User Form State (Name / Agency Name, Zonal Office, Bank, no branch, no target)
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('1234');
  const [newUserPin, setNewUserPin] = useState('1234');
  const [newUserName, setNewUserName] = useState(''); // Name / Agency Name
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserMobile, setNewUserMobile] = useState('');
  const [newUserRole, setNewUserRole] = useState<UserRole>('agent');
  const [newUserBank, setNewUserBank] = useState('All Banks');
  const [newUserZonalOffice, setNewUserZonalOffice] = useState('');

  // Hierarchy first: bank, then zone, then branch, then the rate -- and only
  // then who the person is. Each choice narrows the next, so asking for the
  // name first meant filling the form in an order the data does not follow.
  const [newUserBanks, setNewUserBanks] = useState<string[]>([]);
  const [newUserBranches, setNewUserBranches] = useState<string[]>([]);
  const [newUserCommissionPct, setNewUserCommissionPct] = useState<string>('');
  // Free Demo / Subscription — agents only.
  const [newUserPlan, setNewUserPlan] = useState<AgentPlan>(emptyAgentPlan);
  // Every reason Create User is refused, listed on the form instead of one
  // alert at a time. An administrator used to fix one omission, press Create
  // again, and be told about the next.
  const [newUserProblems, setNewUserProblems] = useState<string[]>([]);
  // Shown once, straight after creation, because the password cannot be read
  // back afterwards -- only reset.
  const [createdCredentials, setCreatedCredentials] = useState<{
    name: string;
    loginId: string;
    password: string;
  } | null>(null);

  // The rate follows the chosen bank unless it has been typed over.
  const [commissionPctTouched, setCommissionPctTouched] = useState(false);
  useEffect(() => {
    if (commissionPctTouched) return;
    const primary = newUserBanks[0];
    if (!primary) {
      setNewUserCommissionPct('');
      return;
    }
    const bank = banks.find(
      (b) => b.name?.trim().toLowerCase() === primary.trim().toLowerCase()
    );
    const rate =
      typeof bank?.defaultCommissionRate === 'number' && bank.defaultCommissionRate > 0
        ? bank.defaultCommissionRate
        : commissionSettings.defaultRate ?? 10;
    setNewUserCommissionPct(String(rate));
  }, [newUserBanks, banks, commissionSettings.defaultRate, commissionPctTouched]);

  // Recovery Reversal Modal State
  const [isReversalModalOpen, setIsReversalModalOpen] = useState(false);
  const [reversalTargetRecovery, setReversalTargetRecovery] = useState<RecoveryRecord | null>(null);
  const [reversalReasonInput, setReversalReasonInput] = useState('');
  const [isProcessingReversal, setIsProcessingReversal] = useState(false);

  // Edit User Modal State
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editUserName, setEditUserName] = useState('');
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserMobile, setEditUserMobile] = useState('');
  const [editUserRole, setEditUserRole] = useState<UserRole>('agent');
  const [editUserBank, setEditUserBank] = useState('All Banks');
  /**
   * The agent's plan, editable after creation.
   *
   * Batch 25 stored Free Demo / Subscription at user creation and nothing
   * could change it afterwards -- so a demo that needed extending, or a demo
   * customer who decided to subscribe, meant deleting the user and making a
   * new one, which loses their Agent ID and everything allocated to it.
   */
  const [editUserPlan, setEditUserPlan] = useState<AgentPlan>(emptyAgentPlan);
  const [editUserAgentId, setEditUserAgentId] = useState('');
  const [editLoginId, setEditLoginId] = useState('');
  const [changingLoginId, setChangingLoginId] = useState(false);
  const [loginIdNote, setLoginIdNote] = useState('');

  /**
   * The ID that ACTUALLY signs in, read from the hidden auth address rather
   * than from the profile fields -- because when the two disagree, it is the
   * auth address that decides whether somebody gets in.
   */
  const actualSignInId = useMemo(() => {
    const email = (editingUser as any)?.authEmail as string | undefined;
    if (email && email.includes('@')) return email.split('@')[0];
    return (
      (editingUser as any)?.loginId ||
      editingUser?.username ||
      (editingUser as any)?.userId ||
      ''
    );
  }, [editingUser]);

  const signInIdMismatch = useMemo(() => {
    if (!editingUser || !actualSignInId) return false;
    const claimed = ((editingUser as any).loginId || editingUser.username || '').trim();
    if (!claimed) return false;
    return claimed.toLowerCase() !== actualSignInId.toLowerCase();
  }, [editingUser, actualSignInId]);
  const [editUserBranch, setEditUserBranch] = useState('');
  /**
   * Every branch this user covers.
   *
   * Creation has had a tick list since Batch 25; editing only had a single
   * "Assigned Branch" dropdown. So opening an existing multi-branch user and
   * pressing Update silently reduced them to one branch -- the save wrote
   * `branch` and never touched `branches`.
   */
  const [editUserBranches, setEditUserBranches] = useState<string[]>([]);
  const [editUserZone, setEditUserZone] = useState('');
  const [editUserTarget, setEditUserTarget] = useState(300000);

  // Change Password Modal State
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [passwordTargetUser, setPasswordTargetUser] = useState<User | null>(null);
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [newPinInput, setNewPinInput] = useState('1234');
  const [userAdminFeedback, setUserAdminFeedback] = useState<string | null>(null);

  // Delete User Confirmation Modal State
  const [isDeleteUserOpen, setIsDeleteUserOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);

  // Delete Customer Account Confirmation Modal State
  const [isDeleteAccountOpen, setIsDeleteAccountOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<Account | null>(null);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // Close / Delete Account Modal State (Closed as per OTS / Regular Close / Deletion)
  const [isCloseAccountModalOpen, setIsCloseAccountModalOpen] = useState(false);
  const [accountToClose, setAccountToClose] = useState<Account | null>(null);

  // Set account category (admin) without re-uploading a sheet
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

  // Bulk Delete Accounts Modal State (Agent / Branch / All / Selected)
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [bulkDeleteInitialScope, setBulkDeleteInitialScope] = useState<'all' | 'agent' | 'branch' | 'selected'>('agent');
  const [bulkDeletePreselectedAgent, setBulkDeletePreselectedAgent] = useState<string | undefined>(undefined);
  const [bulkDeletePreselectedBranch, setBulkDeletePreselectedBranch] = useState<string | undefined>(undefined);

  const openBulkDeleteModal = (
    scope: 'all' | 'agent' | 'branch' | 'selected' = 'agent',
    agentId?: string,
    branchName?: string
  ) => {
    setBulkDeleteInitialScope(scope);
    setBulkDeletePreselectedAgent(agentId);
    setBulkDeletePreselectedBranch(branchName);
    setIsBulkDeleteModalOpen(true);
  };

  const openCloseAccountModal = (acc: Account) => {
    setAccountToClose(acc);
    setIsCloseAccountModalOpen(true);
  };

  // Cloud Firestore Collection Viewer tab
  const [activeCollectionTab, setActiveCollectionTab] = useState<string>('Accounts');
  const [firestoreStats, setFirestoreStats] = useState(() => firebaseFirestoreService.getStats());
  const [isTestingFirestore, setIsTestingFirestore] = useState(false);
  const [firestoreTestMsg, setFirestoreTestMsg] = useState<string | null>(null);

  useEffect(() => {
    const unsub = firebaseFirestoreService.subscribeStats(setFirestoreStats);
    return unsub;
  }, []);

  // Leaving a screen clears what was typed and chosen on it.
  //
  // Search text and every filter used to survive the move, so opening
  // Accounts after searching somewhere else showed a list already narrowed by
  // a term that was no longer on screen -- it read as missing data. The
  // dashboard's own filters are deliberately left alone here; they have their
  // own reset button and are meant to persist while the dashboard is open.
  useEffect(() => {
    setSearchInput('');
    setSearchTerm('');
    setFilterZone('ALL');
    setFilterBranch('ALL');
    setFilterStatus('ALL');
    setFilterStarRating('ALL');
    setPtpFilterStatus('ALL');
    setSelectedAccountIds([]);
    setIsMobileSidebarOpen(false);
  }, [activeSection]);

  const handleTestFirestoreConnection = async () => {
    setIsTestingFirestore(true);
    setFirestoreTestMsg(null);
    try {
      const res = await firebaseFirestoreService.testConnection();
      setFirestoreTestMsg(`Firestore latency: ${res.latencyMs}ms • Status: ${res.connected ? 'Connected' : 'Offline'}`);
    } catch (err: any) {
      setFirestoreTestMsg(`Test failed: ${err?.message || 'Error'}`);
    } finally {
      setIsTestingFirestore(false);
    }
  };

  // Months list for dropdown filter
  const availableDashMonths = useMemo(() => {
    const set = new Set<string>();
    set.add(currentMonthStr);
    scopedRecoveries.forEach((r) => {
      if (r.recoveryDate && r.recoveryDate.length >= 7) {
        set.add(r.recoveryDate.slice(0, 7));
      }
    });
    return Array.from(set).sort().reverse();
  }, [scopedRecoveries, currentMonthStr]);

  // Summary Metrics (with multi-dimension filtering support for Admin Dashboard)
  const isDashFilterActive =
    dashUserFilter !== 'ALL' ||
    dashBankFilter !== 'ALL' ||
    dashZoneFilter !== 'ALL' ||
    dashBranchFilter !== 'ALL' ||
    dashMonthFilter !== currentMonthStr;

  const resetDashFilters = () => {
    setDashUserFilter('ALL');
    setDashBankFilter('ALL');
    setDashZoneFilter('ALL');
    setDashBranchFilter('ALL');
    setDashMonthFilter(currentMonthStr);
  };

  const filteredDashAccounts = useMemo(() => {
    return scopedAccounts.filter((acc) => {
      if (dashUserFilter !== 'ALL') {
        const matchAgent =
          acc.assignedAgentId === dashUserFilter ||
          acc.assignedAgentName?.toLowerCase() === dashUserFilter.toLowerCase() ||
          acc.assignedUserId === dashUserFilter;
        if (!matchAgent) return false;
      }
      if (dashBankFilter !== 'ALL') {
        const matchBank =
          (acc.bankName && acc.bankName.toLowerCase() === dashBankFilter.toLowerCase()) ||
          (acc.bank && acc.bank.toLowerCase() === dashBankFilter.toLowerCase());
        if (!matchBank) return false;
      }
      if (dashZoneFilter !== 'ALL') {
        const matchZone =
          (acc.zone && acc.zone.toLowerCase() === dashZoneFilter.toLowerCase()) ||
          (acc.zonalOffice && acc.zonalOffice.toLowerCase() === dashZoneFilter.toLowerCase());
        if (!matchZone) return false;
      }
      if (dashBranchFilter !== 'ALL') {
        const matchBranch =
          (acc.branch && acc.branch.toLowerCase() === dashBranchFilter.toLowerCase()) ||
          (acc.branchCode && acc.branchCode.toLowerCase() === dashBranchFilter.toLowerCase());
        if (!matchBranch) return false;
      }
      return true;
    });
  }, [scopedAccounts, dashUserFilter, dashBankFilter, dashZoneFilter, dashBranchFilter]);

  const filteredDashAccountIds = useMemo(() => {
    return new Set(filteredDashAccounts.map((a) => a.accountId));
  }, [filteredDashAccounts]);

  const filteredDashRecoveries = useMemo(() => {
    return scopedRecoveries.filter((r) => {
      if (r.isReversed) return false;
      if (dashMonthFilter !== 'ALL' && (!r.recoveryDate || !r.recoveryDate.startsWith(dashMonthFilter))) {
        return false;
      }
      if (!isDashFilterActive) return true;
      if (filteredDashAccountIds.has(r.accountId)) return true;
      if (dashUserFilter !== 'ALL' && (r.agentId === dashUserFilter || r.agentName === dashUserFilter)) return true;
      return false;
    });
  }, [scopedRecoveries, dashMonthFilter, isDashFilterActive, filteredDashAccountIds, dashUserFilter]);

  const filteredDashPtps = useMemo(() => {
    return scopedPtps.filter((p) => {
      if (dashMonthFilter !== 'ALL' && (!p.ptpDate || !p.ptpDate.startsWith(dashMonthFilter))) {
        return false;
      }
      if (!isDashFilterActive) return true;
      if (filteredDashAccountIds.has(p.accountId)) return true;
      if (dashUserFilter !== 'ALL' && (p.agentId === dashUserFilter || p.agentName === dashUserFilter)) return true;
      return false;
    });
  }, [scopedPtps, dashMonthFilter, isDashFilterActive, filteredDashAccountIds, dashUserFilter]);

  const filteredDashVisits = useMemo(() => {
    return scopedVisits.filter((v) => {
      if (dashMonthFilter !== 'ALL' && (!v.visitDate || !v.visitDate.startsWith(dashMonthFilter))) {
        return false;
      }
      if (!isDashFilterActive) return true;
      if (filteredDashAccountIds.has(v.accountId)) return true;
      if (dashUserFilter !== 'ALL' && (v.agentId === dashUserFilter || v.agentName === dashUserFilter)) return true;
      return false;
    });
  }, [scopedVisits, dashMonthFilter, isDashFilterActive, filteredDashAccountIds, dashUserFilter]);

  const filteredDashCommissions = useMemo(() => {
    return scopedCommissions.filter((c) => {
      if (dashMonthFilter !== 'ALL' && (!c.date || !c.date.startsWith(dashMonthFilter))) {
        return false;
      }
      return true;
    });
  }, [scopedCommissions, dashMonthFilter]);

  const totalRecoverySum = useMemo(() => filteredDashRecoveries.reduce((sum, r) => sum + r.amount, 0), [filteredDashRecoveries]);
  const totalCommissionSum = useMemo(() => filteredDashCommissions.reduce((sum, r) => sum + r.commissionAmount, 0), [filteredDashCommissions]);
  const totalOverdueSum = useMemo(() => filteredDashAccounts.reduce((sum, a) => sum + a.overdueAmount, 0), [filteredDashAccounts]);
  const totalOutstandingSum = useMemo(() => filteredDashAccounts.reduce((sum, a) => sum + a.outstandingAmount, 0), [filteredDashAccounts]);

  const activeAgents = useMemo(() => {
    return scopedUsers.filter((u) => {
      if (u.role !== 'agent' || !u.active) return false;
      if (dashUserFilter !== 'ALL') {
        return u.agentId === dashUserFilter || u.id === dashUserFilter || u.name === dashUserFilter;
      }
      if (dashZoneFilter !== 'ALL') {
        return (
          (u.zonalOffice && u.zonalOffice.toLowerCase() === dashZoneFilter.toLowerCase()) ||
          (u.zone && u.zone.toLowerCase() === dashZoneFilter.toLowerCase())
        );
      }
      if (dashBranchFilter !== 'ALL') {
        return u.branch && u.branch.toLowerCase() === dashBranchFilter.toLowerCase();
      }
      return true;
    });
  }, [scopedUsers, dashUserFilter, dashZoneFilter, dashBranchFilter]);

  const ptpPreview = getExpiredPTPPreview(todayIso());

  // Filtered accounts -- the WHOLE matching set, sorted. Select-all, bulk
  // actions and the totals all work on this, not on the visible page.
  const filteredAccounts = useMemo(
    () => applyAccountFilters(scopedAccounts, acctFilters),
    [scopedAccounts, acctFilters]
  );

  /**
   * Allocation filters -- your line 46.
   *
   * Re-allocation used to work off whatever the Loan Accounts filter bar
   * happened to be set to, which meant going to another screen to narrow the
   * list and coming back. These are the allocation screen's own: bank, several
   * branches, and several current assignees at once, which is what "move
   * everything of these two agents in these three branches" needs.
   */
  const [allocBanks, setAllocBanks] = useState<string[]>([]);
  const [allocZones, setAllocZones] = useState<string[]>([]);
  const [allocBranches, setAllocBranches] = useState<string[]>([]);
  const [allocAgents, setAllocAgents] = useState<string[]>([]);
  const [allocPage, setAllocPage] = useState(1);
  const [allocCheck, setAllocCheck] = useState<string>('');

  /**
   * How many of the accounts on screen carry no bank at all.
   *
   * Accounts uploaded from Excel before the bank selector existed have a
   * branch and a zone but no bank, because the importer never wrote that
   * field. There is nothing wrong with those accounts -- they are simply
   * unclassified -- but it is worth saying so on screen, because the
   * remedy (Settings -> fill in bank from branch) is one button.
   */
  const allocBanklessCount = useMemo(
    () =>
      filteredAccounts.filter(
        (a) => !String((a as any).bankName || (a as any).bank || '').trim()
      ).length,
    [filteredAccounts]
  );

  /**
   * ALLOCATION FILTERS FOLLOW THE HIERARCHY: BANK -> ZONAL OFFICE -> BRANCH,
   * AND THEN, OPTIONALLY, WHO HOLDS THE ACCOUNTS.
   *
   * Each list is narrowed by the one before it, so a zone that belongs to no
   * ticked bank is not offered, and a branch that belongs to no ticked zone
   * is not offered. Picking a branch that was not inside the chosen bank was
   * the quickest way to allocate the wrong accounts.
   *
   * Ticking nothing at a level means "all of them", so the screen still
   * opens with everything in view. A blank field on an account is not a
   * mismatch, for the same reason as the bank below: accounts imported
   * before these fields existed carry nothing there and must not vanish.
   */
  /**
   * WHERE THESE LISTS COME FROM, AND WHY IT CHANGED.
   *
   * They used to be worked out from the accounts currently LOADED, with a
   * fallback: if no loaded account matched the bank you ticked, the list
   * gave up and offered everything. Tick Central Bank of India with no CBI
   * accounts loaded and you were offered Bank of Maharashtra's zonal office
   * and every branch in the system -- the one combination that can only
   * ever allocate the wrong accounts.
   *
   * They now come from the HIERARCHY, which is the record of which zone and
   * which branch belong to which bank, whatever happens to be loaded. The
   * zones and branches seen on the loaded accounts are added on top, so an
   * older account whose branch was never created as a record still appears.
   *
   * THERE IS NO FALLBACK ANY MORE. A bank with nothing under it shows an
   * empty list and the screen says so, which is the truth.
   */
  const allocZoneOptions = useMemo(() => {
    if (allocBanks.length === 0) return allUniqueZones;
    const bankSet = new Set(allocBanks.map((b) => b.trim().toLowerCase()));

    // 1. Zones recorded under these banks.
    const fromHierarchy = allocBanks.flatMap((bank) =>
      zonesOfBank(zones, bank).map((z) => (z.name || '').trim())
    );
    // 2. Zones seen on accounts of these banks.
    const fromAccounts = filteredAccounts
      .filter((a) => {
        const bank = String((a as any).bankName || (a as any).bank || '').trim().toLowerCase();
        return bank && bankSet.has(bank);
      })
      .map((a) => String(a.zone || '').trim());

    const live = new Set(
      [...fromHierarchy, ...fromAccounts].filter(Boolean).map((z) => z.toLowerCase())
    );
    return allUniqueZones.filter((z) => live.has(z.trim().toLowerCase()));
  }, [allUniqueZones, allocBanks, zones, filteredAccounts]);

  const allocBranchOptions = useMemo(() => {
    if (allocBanks.length === 0 && allocZones.length === 0) return allUniqueBranches;
    const bankSet = new Set(allocBanks.map((b) => b.trim().toLowerCase()));
    const zoneSet = new Set(allocZones.map((z) => z.trim().toLowerCase()));

    // 1. Branches recorded under these banks (and zones, when chosen).
    const bankList = allocBanks.length ? allocBanks : [undefined as unknown as string];
    const zoneList = allocZones.length ? allocZones : [undefined as unknown as string];
    const fromHierarchy: string[] = [];
    bankList.forEach((bank) => {
      zoneList.forEach((zone) => {
        branchesOfBankZone(branches, bank, zone, banks).forEach((br) =>
          fromHierarchy.push((br.name || '').trim())
        );
      });
    });

    // 2. Branches seen on accounts of those banks and zones.
    const fromAccounts = filteredAccounts
      .filter((a) => {
        const bank = String((a as any).bankName || (a as any).bank || '').trim().toLowerCase();
        if (bankSet.size && !(bank && bankSet.has(bank))) return false;
        const zone = String(a.zone || '').trim().toLowerCase();
        if (zoneSet.size && !(zone && zoneSet.has(zone))) return false;
        return true;
      })
      .map((a) => String(a.branch || '').trim());

    const live = new Set(
      [...fromHierarchy, ...fromAccounts].filter(Boolean).map((b) => b.toLowerCase())
    );
    return allUniqueBranches.filter((b) => live.has(b.trim().toLowerCase()));
  }, [allUniqueBranches, allocBanks, allocZones, branches, banks, filteredAccounts]);

  /**
   * The agents worth offering in "Currently held by": only those attached to
   * the bank -- and the branch -- that has been ticked. Picking a bank and
   * then being offered the other bank's agents is how an account ends up
   * with somebody who does not work on it.
   */
  const allocAgentOptions = useMemo(() => {
    const agents = users.filter((u) => u.role === 'agent');
    if (allocBanks.length === 0 && allocBranches.length === 0) return agents;
    const bankSet = new Set(allocBanks.map((b) => b.trim().toLowerCase()));

    return agents.filter((u) => {
      if (bankSet.size) {
        const theirBanks = [u.bank, ...((u.banks as string[] | undefined) || [])]
          .map((b) => (b || '').trim().toLowerCase())
          .filter(Boolean);
        // No bank recorded: an older record, left visible rather than hidden.
        if (theirBanks.length && !theirBanks.some((b) => bankSet.has(b))) return false;
      }
      if (allocBranches.length) {
        const bankForBranch = allocBanks.length === 1 ? allocBanks[0] : '';
        return allocBranches.some((br) => userInBranch(u, br, bankForBranch, branches));
      }
      return true;
    });
  }, [users, allocBanks, allocBranches, branches]);

  // A zone or branch that is no longer on offer stops filtering, otherwise a
  // tick left behind by an earlier bank silently empties the list.
  useEffect(() => {
    setAllocZones((prev) => prev.filter((z) => allocZoneOptions.includes(z)));
  }, [allocZoneOptions]);
  useEffect(() => {
    setAllocBranches((prev) => prev.filter((b) => allocBranchOptions.includes(b)));
  }, [allocBranchOptions]);
  useEffect(() => {
    const live = new Set(allocAgentOptions.map((u) => u.agentId || u.id));
    setAllocAgents((prev) => prev.filter((a) => a === 'unassigned' || live.has(a)));
  }, [allocAgentOptions]);

  const allocationCandidates = useMemo(() => {
    const bankSet = new Set(allocBanks.map((b) => b.trim().toLowerCase()));
    const zoneSet = new Set(allocZones.map((z) => z.trim().toLowerCase()));
    const branchSet = new Set(allocBranches.map((b) => b.trim().toLowerCase()));
    const agentSet = new Set(allocAgents.map((a) => a.trim().toLowerCase()));

    return filteredAccounts.filter((a) => {
      if (bankSet.size > 0) {
        const bank = String((a as any).bankName || (a as any).bank || '').trim().toLowerCase();
        /**
         * A blank bank is NOT a mismatch.
         *
         * Ticking a bank used to exclude every account whose bank field was
         * empty -- which was all 412 of them, because the Excel importer
         * never wrote a bank. So filtering by bank came up with nothing
         * while filtering by branch alone worked, and it looked as though
         * the accounts had gone. An unclassified account now matches
         * whichever bank you tick, exactly as it already did for branch
         * scoping elsewhere in the app.
         */
        if (bank && !bankSet.has(bank)) return false;
      }
      if (zoneSet.size > 0) {
        // Same rule as the bank: an account with no zone recorded is
        // unclassified, not excluded.
        const zone = String(a.zone || '').trim().toLowerCase();
        if (zone && !zoneSet.has(zone)) return false;
      }
      if (branchSet.size > 0) {
        if (!branchSet.has(String(a.branch || '').trim().toLowerCase())) return false;
      }
      if (agentSet.size > 0) {
        const assigned = String(a.assignedAgentId || '').trim().toLowerCase();
        const assignedName = String(a.assignedAgentName || '').trim().toLowerCase();
        // "Unassigned" is a real choice -- it is usually the whole point.
        const isUnassigned = !assigned && !assignedName;
        if (agentSet.has('unassigned') && isUnassigned) return true;
        if (!agentSet.has(assigned) && !agentSet.has(assignedName)) return false;
      }
      return true;
    });
  }, [filteredAccounts, allocBanks, allocZones, allocBranches, allocAgents]);

  /**
   * The allocation list is paged at 25, like Loan Accounts.
   *
   * Paging here saves no database reads -- the accounts are already in memory
   * from the scope that was loaded, and Firestore bills fetching, not
   * drawing. It is done because four hundred rows in one scroll is not a list
   * anyone can work through, and because a "select all" sitting above an
   * endless scroll invites allocating far more than was meant.
   */
  // `pageOf` returns a PAGE -- { rows, page, pageCount, total, from, to } --
  // not a bare array. Its rows are what gets drawn.
  const allocPaged = useMemo(
    () => pageOf(allocationCandidates, allocPage, ACCOUNTS_PER_PAGE),
    [allocationCandidates, allocPage]
  );
  const pagedAllocationCandidates = allocPaged.rows;
  const allocTotalPages = allocPaged.pageCount;

  // Narrowing the filters must not leave you stranded on page 9 of 3.
  useEffect(() => {
    setAllocPage((p) => Math.min(Math.max(1, p), allocTotalPages));
  }, [allocTotalPages]);

  /** Ids on the page in front of you, which is what the header tick covers. */
  const allocPageIds = useMemo(
    () => pagedAllocationCandidates.map((a) => a.accountId),
    [pagedAllocationCandidates]
  );
  const allocPageAllSelected =
    allocPageIds.length > 0 && allocPageIds.every((id) => selectedAccountIds.includes(id));

  /**
   * ONE ROW PER CUSTOMER, NOT ONE PER LOAN. See groupAccountsByCustomer.
   *
   * Grouped after filtering and before paging, so a search still matches a
   * customer by any of their loans and a page holds 25 customers. Selecting
   * a row selects EVERY loan behind it -- the row is the borrower, so a bulk
   * action on it has to act on all of them or it would silently do a third
   * of the job.
   */
  const customerGroups = useMemo(
    () => groupAccountsByCustomer(filteredAccounts),
    [filteredAccounts]
  );

  // The 25 rows actually drawn.
  const pagedAccounts = useMemo(
    () => pageOf(customerGroups, acctPage, ACCOUNTS_PER_PAGE),
    [customerGroups, acctPage]
  );

  // Leaving Accounts clears the search.
  //
  // Coming back used to show the previous search still typed in, with its
  // results still on screen, and no indication that this was not the full
  // list -- so a customer that was not in the previous search looked as
  // though they did not exist.
  useEffect(() => {
    if (activeSection !== 'accounts') {
      setAcctFilters((prev) =>
        prev.text || prev.minAmount || prev.maxAmount || prev.rating || prev.status || prev.branch || prev.zone || prev.agent
          ? { ...emptyAccountFilters, sort: prev.sort }
          : prev
      );
      setAcctPage(1);
    }
  }, [activeSection]);

  const handleSelectAll = () => {
    if (selectedAccountIds.length === filteredAccounts.length) {
      setSelectedAccountIds([]);
    } else {
      setSelectedAccountIds(filteredAccounts.map((a) => a.accountId));
    }
  };

  const handleToggleSelect = (id: string) => {
    if (selectedAccountIds.includes(id)) {
      setSelectedAccountIds(selectedAccountIds.filter((x) => x !== id));
    } else {
      setSelectedAccountIds([...selectedAccountIds, id]);
    }
  };

  const handleBulkAllocate = () => {
    if (selectedAccountIds.length === 0) {
      alert('Please select accounts to allocate.');
      return;
    }
    /**
     * NEVER GUESS WHO THE ACCOUNTS GO TO.
     *
     * This used to end in `|| users[0]` -- if the lookup failed it silently
     * fell back to the FIRST user in the list, which is the administrator. And
     * the lookup failed routinely, because the dropdown starts empty: a
     * <select> whose value matches no option still DISPLAYS the first one, so
     * an administrator who did not touch it saw "ScaleSupport (RA-0001)" on
     * screen while the value held ''. Four hundred accounts then went to the
     * admin, and the message said so, which is the only reason it was noticed.
     */
    if (!selectedAgentForAllocation) {
      alert('Choose which agent these accounts go to first.');
      return;
    }

    const agent = users.find(
      (u) =>
        (u.agentId && u.agentId === selectedAgentForAllocation) ||
        u.id === selectedAgentForAllocation
    );
    if (!agent) {
      alert(
        'That agent could not be found. They may have been deleted or renamed — pick again from the list.'
      );
      return;
    }

    // Re-allocating a book is not a small action; it is named and counted
    // before it happens.
    const ok = window.confirm(
      `Allocate ${selectedAccountIds.length} account(s) to:\n\n` +
        `${agent.name} (${agent.agentId || agent.id})\n` +
        `${agent.branch ? `Branch: ${agent.branch}\n` : ''}\n` +
        'Any accounts currently held by someone else will move to this agent.'
    );
    if (!ok) return;

    allocateBulkAccounts(selectedAccountIds, agent.id, agent.name, agent.agentId || agent.id);
    const moved = selectedAccountIds.length;
    setSelectedAccountIds([]);
    alert(`Allocated ${moved} account(s) to ${agent.name} (${agent.agentId || agent.id}).`);
  };

  const [deletingPhotoId, setDeletingPhotoId] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);

  // Filling in a forgotten note after the fact. Allowed once per photo for
  // the agent who took it; the context enforces that rule.
  const handleEditPhotoNote = async (ph: any) => {
    const id = ph.id || ph.photoId;
    const next = window.prompt(
      ph.agentRemark
        ? 'Correct the note on this photo:'
        : 'Add the note for this photo (this can only be done once):',
      ph.agentRemark || ''
    );
    if (next === null) return;

    setEditingNoteId(id);
    try {
      const res = await updatePhotoNote(id, next);
      if (!res.success) {
        alert(res.message);
        return;
      }
      setUserAdminFeedback(`Note saved for photo ${ph.photoId}.`);
      setTimeout(() => setUserAdminFeedback(null), 5000);
    } finally {
      setEditingNoteId(null);
    }
  };

  const handleDeletePhoto = async (ph: any) => {
    const id = ph.id || ph.photoId;
    const ok = window.confirm(
      `Delete this geo-tagged photo?\n\nAccount: ${ph.accountId}\nCaptured by: ${ph.agentName} on ${ph.date}\n\nThis cannot be undone. The deletion is recorded in the activity log.`
    );
    if (!ok) return;

    const reason = window.prompt('Reason for deleting this photo (optional):') || '';

    setDeletingPhotoId(id);
    try {
      const res = await deleteGeoTaggedPhoto(id, reason);
      if (!res.success) {
        alert(res.message);
        return;
      }
      setUserAdminFeedback(`Geo-tagged photo for ${ph.accountId} deleted.`);
      setTimeout(() => setUserAdminFeedback(null), 5000);
    } finally {
      setDeletingPhotoId(null);
    }
  };

  const [isCreatingUser, setIsCreatingUser] = useState(false);

  const handleCreateUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isCreatingUser) return;

    // The Login ID is the staff code the person types to sign in.
    // It must never be an e-mail address.
    const assignedAgentId =
      newUserId.trim() ||
      (newUserRole === 'agent' ? `RA-${Math.floor(1000 + Math.random() * 9000)}` : '');

    if (!assignedAgentId) {
      alert('Please enter a User ID / Agent ID (for example RA-0001). This is what the user types to log in.');
      return;
    }
    if (assignedAgentId.includes('@')) {
      alert('User ID must be a staff code such as RA-0001, not an e-mail address.');
      return;
    }
    if (assignedAgentId.replace(/[^a-zA-Z0-9]/g, '').length < 3) {
      alert('User ID must be at least 3 characters (for example RA-0001).');
      return;
    }

    // No orphan users.
    //
    // Until now a user could be saved with no bank, no zone and no branch, and
    // nothing objected -- the person then signed in and saw nothing, or saw
    // everything, depending on which screen was guessing their scope. Every
    // reason is listed at once rather than one alert per attempt.
    const problems = validateUserHierarchy(
      {
        role: newUserRole,
        banks: newUserBanks,
        zone: newUserZonalOffice,
        branches: newUserBranches,
        agentId: assignedAgentId,
        plan: newUserPlan.plan,
        demoStartDate: newUserPlan.demoStartDate,
        demoEndDate: newUserPlan.demoEndDate,
        subscriptionStartDate: newUserPlan.subscriptionStartDate,
        subscriptionEndDate: newUserPlan.subscriptionEndDate,
        subscriptionAmount: newUserPlan.subscriptionAmount,
        paymentDate: newUserPlan.paymentDate,
        paymentMode: newUserPlan.paymentMode,
      },
      zones,
      branches,
      banks
    );
    if (problems.length > 0) {
      setNewUserProblems(problems);
      return;
    }
    setNewUserProblems([]);

    // The ids are resolved from the names that were actually chosen. Writing
    // the name alone is what let a renamed branch orphan its users; writing a
    // guessed id is what filed them under the wrong bank.
    const primaryBankName = newUserBanks[0] || '';
    const zoneRecord = findZoneByName(zones, newUserZonalOffice, primaryBankName);
    const primaryBranchName = newUserBranches[0] || '';
    const branchRecord = findBranchByName(
      branches,
      primaryBranchName,
      primaryBankName,
      newUserZonalOffice
    );
    const bankRecord = banks.find(
      (b) => (b.name || '').trim().toLowerCase() === primaryBankName.trim().toLowerCase()
    );
    const isAgent = newUserRole === 'agent';

    setIsCreatingUser(true);
    try {
      const result = await addUser({
        bankId: bankRecord?.id || undefined,
        zoneId: zoneRecord?.id || undefined,
        branchId: branchRecord?.id || undefined,
        // Agents only. The other roles are free and carry no plan at all.
        agentPlan: isAgent && newUserPlan.plan ? newUserPlan.plan : undefined,
        demoStartDate: isAgent && newUserPlan.plan === 'demo' ? newUserPlan.demoStartDate : undefined,
        demoEndDate: isAgent && newUserPlan.plan === 'demo' ? newUserPlan.demoEndDate : undefined,
        subscriptionStartDate:
          isAgent && newUserPlan.plan === 'subscription'
            ? newUserPlan.subscriptionStartDate
            : undefined,
        subscriptionEndDate:
          isAgent && newUserPlan.plan === 'subscription'
            ? newUserPlan.subscriptionEndDate
            : undefined,
        subscriptionAmount:
          isAgent && newUserPlan.plan === 'subscription' && Number(newUserPlan.subscriptionAmount) > 0
            ? Number(newUserPlan.subscriptionAmount)
            : undefined,
        paymentDate:
          isAgent && newUserPlan.plan === 'subscription' ? newUserPlan.paymentDate : undefined,
        paymentMode:
          isAgent && newUserPlan.plan === 'subscription' ? newUserPlan.paymentMode : undefined,
        paymentReference:
          isAgent && newUserPlan.plan === 'subscription' && newUserPlan.paymentReference.trim()
            ? newUserPlan.paymentReference.trim()
            : undefined,
        name: newUserName,
        agencyName: newUserName,
        // Optional contact e-mail only -- not used for signing in.
        email: newUserEmail.trim(),
        mobile: newUserMobile,
        role: newUserRole,
        // The first ticked bank is the primary; the full list is what
        // access is judged by.
        bank: newUserBanks[0],
        banks: newUserBanks,
        branch: newUserBranches[0] || '',
        branches: newUserBranches,
        commissionPercent:
          newUserCommissionPct.trim() !== '' && Number(newUserCommissionPct) > 0
            ? Number(newUserCommissionPct)
            : undefined,
        area: '',
        zone: newUserZonalOffice || undefined,
        zonalOffice: newUserZonalOffice || undefined,
        agentId: assignedAgentId,
        username: assignedAgentId,
        password: newUserPassword.trim() || 'Agent@2026',
      });

      setIsCreateUserOpen(false);
      setNewUserId('');
      setNewUserPassword('');
      setNewUserPin('');
      setNewUserName('');
      setNewUserEmail('');
      setNewUserMobile('');
      setNewUserBank('All Banks');
      setNewUserBanks([]);
      setNewUserBranches([]);
      setNewUserZonalOffice('');
      setNewUserCommissionPct('');
      setNewUserPlan(emptyAgentPlan);
      setNewUserProblems([]);
      setCommissionPctTouched(false);

      // Shown once. The password is stored hashed and cannot be read back
      // afterwards, only reset -- so if it is not handed over now, it is gone.
      setCreatedCredentials({
        name: newUserName,
        loginId: result?.loginId || assignedAgentId,
        password: newUserPassword.trim() || 'Agent@2026',
      });
      setNewUserName('');
    } catch (err: any) {
      alert(`Could not create this user: ${err?.message || 'Unknown error'}`);
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleConfirmReversal = async () => {
    if (!reversalTargetRecovery) return;
    setIsProcessingReversal(true);
    try {
      await reverseRecovery(
        reversalTargetRecovery.id,
        reversalReasonInput.trim() || 'Reversed per administrator request'
      );
      setIsReversalModalOpen(false);
      setReversalTargetRecovery(null);
      setReversalReasonInput('');
      setUserAdminFeedback(`Recovery #${reversalTargetRecovery.receiptNumber} successfully reversed.`);
      setTimeout(() => setUserAdminFeedback(null), 5000);
    } catch (err) {
      alert(`Failed to reverse recovery: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsProcessingReversal(false);
    }
  };

  const openEditUserModal = (u: User) => {
    setEditingUser(u);
    setEditUserName(u.name);
    setEditUserEmail(u.contactEmail || '');
    setEditUserMobile(u.mobile || '');
    setEditUserRole(u.role);
    setEditUserBank(u.bank || 'All Banks');
    setEditUserAgentId(u.agentId || '');
    setEditLoginId(
      ((u as any).authEmail || '').includes('@')
        ? String((u as any).authEmail).split('@')[0]
        : (u as any).loginId || u.username || ''
    );
    setLoginIdNote('');
    setEditUserBranch(u.branch || '');
    setEditUserBranches(
      u.branches && u.branches.length > 0 ? u.branches : u.branch ? [u.branch] : []
    );
    setEditUserZone(u.zone || '');
    setEditUserTarget(u.monthlyTarget || 300000);
    setEditUserPlan({
      plan: u.agentPlan || '',
      demoStartDate: u.demoStartDate || '',
      demoEndDate: u.demoEndDate || '',
      subscriptionStartDate: u.subscriptionStartDate || '',
      subscriptionEndDate: u.subscriptionEndDate || '',
      subscriptionAmount:
        typeof u.subscriptionAmount === 'number' ? String(u.subscriptionAmount) : '',
      paymentDate: u.paymentDate || '',
      paymentMode: u.paymentMode || '',
      paymentReference: u.paymentReference || '',
    });
    setIsEditUserOpen(true);
  };

  const handleEditUserSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    updateUser(editingUser.id, {
      name: editUserName,
      email: editUserEmail,
      mobile: editUserMobile,
      role: editUserRole,
      bank: editUserBank === 'All Banks' ? undefined : editUserBank,
      agentId: editUserAgentId.trim() || undefined,
      branch: editUserBranches[0] || editUserBranch || '',
      branches: editUserBranches,
      zone: editUserZone,
      monthlyTarget: editUserRole === 'agent' ? editUserTarget : undefined,
      // Agents only. Changing someone's role away from agent clears the plan,
      // because the other roles are free and a stale end date would show as
      // "Expired" against a user who never had a subscription.
      agentPlan: editUserRole === 'agent' && editUserPlan.plan ? editUserPlan.plan : undefined,
      demoStartDate:
        editUserRole === 'agent' && editUserPlan.plan === 'demo'
          ? editUserPlan.demoStartDate
          : undefined,
      demoEndDate:
        editUserRole === 'agent' && editUserPlan.plan === 'demo'
          ? editUserPlan.demoEndDate
          : undefined,
      subscriptionStartDate:
        editUserRole === 'agent' && editUserPlan.plan === 'subscription'
          ? editUserPlan.subscriptionStartDate
          : undefined,
      subscriptionEndDate:
        editUserRole === 'agent' && editUserPlan.plan === 'subscription'
          ? editUserPlan.subscriptionEndDate
          : undefined,
      subscriptionAmount:
        editUserRole === 'agent' &&
        editUserPlan.plan === 'subscription' &&
        Number(editUserPlan.subscriptionAmount) > 0
          ? Number(editUserPlan.subscriptionAmount)
          : undefined,
      paymentDate:
        editUserRole === 'agent' && editUserPlan.plan === 'subscription'
          ? editUserPlan.paymentDate
          : undefined,
      paymentMode:
        editUserRole === 'agent' && editUserPlan.plan === 'subscription'
          ? editUserPlan.paymentMode
          : undefined,
      paymentReference:
        editUserRole === 'agent' && editUserPlan.plan === 'subscription'
          ? editUserPlan.paymentReference.trim() || undefined
          : undefined,
    });
    setIsEditUserOpen(false);
    setUserAdminFeedback(`User ${editUserName} updated successfully.`);
    setTimeout(() => setUserAdminFeedback(null), 5000);
  };

  const openChangePasswordModal = (u: User) => {
    setPasswordTargetUser(u);
    setNewPasswordInput('');
    setNewPinInput('');
    setIsChangePasswordOpen(true);
  };

  const handleChangePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordTargetUser) return;
    const pwd = newPasswordInput.trim();
    if (pwd.length < 4) {
      alert('Password must be at least 4 characters.');
      return;
    }

    const res = await changeUserPassword(passwordTargetUser.id, pwd);
    if (!res.success) {
      alert(`Could not update the password: ${res.error || 'Unknown error'}`);
      return;
    }

    setIsChangePasswordOpen(false);
    setUserAdminFeedback(`Password successfully updated for user "${passwordTargetUser.name}" (ID: ${passwordTargetUser.agentId || passwordTargetUser.id}). Previous sessions have been revoked.`);
    setTimeout(() => setUserAdminFeedback(null), 6000);
  };

  const openDeleteUserModal = (u: User) => {
    setUserToDelete(u);
    setIsDeleteUserOpen(true);
  };

  const handleConfirmDeleteUser = async () => {
    if (!userToDelete) return;
    setIsDeletingUser(true);
    try {
      const res = await deleteUser(userToDelete.id);
      setIsDeleteUserOpen(false);
      setUserAdminFeedback(res.message);
      setUserToDelete(null);
      setTimeout(() => setUserAdminFeedback(null), 7000);
    } catch (err: any) {
      alert(err?.message || 'Failed to delete user.');
    } finally {
      setIsDeletingUser(false);
    }
  };

  const openDeleteAccountModal = (acc: Account) => {
    setAccountToDelete(acc);
    setIsDeleteAccountOpen(true);
  };

  const handleConfirmDeleteAccount = async () => {
    if (!accountToDelete) return;
    setIsDeletingAccount(true);
    try {
      // MUST be awaited. This used to fire the delete and immediately report
      // success, throwing away the result -- so a refused delete looked like
      // a completed one and the account simply came back on the next sync.
      // The try/catch here could never catch it either: an un-awaited
      // promise rejects outside the block.
      const res = await deleteAccount(accountToDelete.accountId);
      if (!res?.success) {
        alert(res?.message || 'Could not delete this account.');
        return;
      }
      setIsDeleteAccountOpen(false);
      // Always let go of the deleted account. Anything still pointing at a
      // record that no longer exists is what turns a delete into a blank
      // screen a moment later.
      setIsSingleCustomerModalOpen(false);
      setSelectedAccount(null);
      setSelectedAccountIds((prev) =>
        prev.filter((id) => id !== accountToDelete.accountId)
      );
      setUserAdminFeedback(`Customer account "${accountToDelete.customerName}" (${accountToDelete.accountId}) was deleted.`);
      setAccountToDelete(null);
      if (selectedAccount?.accountId === accountToDelete.accountId) {
        setIsSingleCustomerModalOpen(false);
        setSelectedAccount(null);
      }
      setTimeout(() => setUserAdminFeedback(null), 6000);
    } catch (err: any) {
      alert(err?.message || 'Failed to delete customer account.');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const [isDeleteAllAccountsModalOpen, setIsDeleteAllAccountsModalOpen] = useState(false);
  const [isDeletingAllAccounts, setIsDeletingAllAccounts] = useState(false);

  const handleConfirmDeleteAllAccounts = async () => {
    setIsDeletingAllAccounts(true);
    try {
      const res = await deleteAllAccountsFromStorage();
      setIsDeleteAllAccountsModalOpen(false);
      setUserAdminFeedback(res.message);
      setSelectedAccount(null);
      setIsSingleCustomerModalOpen(false);
      setTimeout(() => setUserAdminFeedback(null), 8000);
    } catch (err: any) {
      alert(err?.message || 'Failed to delete all account data from storage.');
    } finally {
      setIsDeletingAllAccounts(false);
    }
  };

  const openAccountModal = (acc: Account, type: string) => {
    setSelectedAccount(acc);
    if (type === 'timeline') setIsTimelineOpen(true);
    else if (type === 'singleView') setIsSingleCustomerModalOpen(true);
    else if (type === 'note') setIsAddNoteModalOpen(true);
    else if (type === 'watermark') setIsWatermarkOpen(true);
    else if (type === 'voice') setIsVoiceOpen(true);
    else if (type === 'followup') setIsFollowUpOpen(true);
    else if (type === 'ptp') setIsPTPOpen(true);
    else if (type === 'recovery') setIsRecoveryOpen(true);
    else if (type === 'visit') setIsVisitOpen(true);
    else if (type === 'doc') setIsDocOpen(true);
    else if (type === 'whatsapp') setIsWhatsAppOpen(true);
    else if (type === 'ai') setIsAIOpen(true);
  };

  // If current logged-in user is an Agent, render the AgentWebPortal directly for their web workspace
  if (currentUser.role === 'agent') {
    return <AgentWebPortal />;
  }

  // If Admin/Manager switched to view Agent Desk
  if (activeSection === 'agent-desk') {
    return (
      <div className="flex flex-col h-[calc(100vh-53px)] w-full">
        <div className="bg-slate-900 text-white px-6 py-2 flex items-center justify-between text-xs border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded font-bold border border-blue-500/30">
              Admin Preview
            </span>
            <span>You are viewing the <b>Agent Web Console</b> workspace.</span>
          </div>
          <button
            onClick={() => setActiveSection('dashboard')}
            className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg font-semibold border border-slate-700 transition cursor-pointer"
          >
            ← Back to Management Portal
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          <AgentWebPortal />
        </div>
      </div>
    );
  }

  const renderNavContent = () => (
    <>
      <div className="p-4 sm:p-5 border-b border-slate-800 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="bg-white/95 px-3 py-2.5 rounded-xl shadow-2xs">
            <ScaleSupportLogo variant="full" size="2xl" />
          </div>
          {isMobileSidebarOpen && (
            <button
              onClick={() => setIsMobileSidebarOpen(false)}
              className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between px-1">
          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
            {currentUser.role} Workspace
          </span>
          <span className="text-[10px] bg-emerald-500/20 text-emerald-300 font-mono px-1.5 py-0.5 rounded border border-emerald-500/30">
            Live Core
          </span>
        </div>
      </div>

      {/* Sidebar Nav Items */}
      <nav className="flex-1 py-3 overflow-y-auto">
        <div className="px-5 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
          Operations
        </div>

        {[
          { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
          // Shortlist Customers menu removed. Shortlisting by rating, amount
          // range, name, agent, branch and account number now lives in the
          // bar above the Loan Accounts list, where the accounts are -- so
          // narrowing a list no longer means leaving the screen and coming
          // back. The section itself is left in the file; nothing routes to it.
          { id: 'accounts', label: 'Loan Accounts', icon: FolderLock, count: scopedAccounts.length },
          { id: 'allocation', label: 'Account Allocation', icon: Users },
          { id: 'followups', label: 'Follow-up Logs', icon: PhoneCall, count: followups.length },
          { id: 'ptp', label: 'PTP Commitments', icon: Calendar, count: scopedPtps.length },
          { id: 'visits', label: 'Field Visits', icon: MapPin, count: scopedVisits.length },
          { id: 'messages', label: 'Messages', icon: Inbox, count: unreadMessageCount },
        ].map((item) => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          return (
            <button
              key={item.id}
              onClick={() => {
                setActiveSection(item.id);
                setIsMobileSidebarOpen(false);
              }}
              className={`w-full flex items-center justify-between px-5 py-2.5 transition-colors cursor-pointer text-left text-sm ${
                isActive
                  ? 'bg-blue-600/10 text-blue-400 border-r-4 border-blue-500 font-medium'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon className="w-4 h-4 shrink-0" />
                <span>{item.label}</span>
              </div>
              {item.count !== undefined && (
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-medium ${
                    isActive ? 'bg-blue-600/30 text-blue-300' : 'bg-slate-800 text-slate-400'
                  }`}
                >
                  {item.count}
                </span>
              )}
            </button>
          );
        })}

        <div className="px-5 mt-4 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
          Hierarchy &amp; Governance
        </div>

        {[
          { id: 'hierarchy', label: 'Hierarchy & Branches', icon: Network, count: branches.length },
          // Commission rates decide what every agent is paid, and Users is
          // where logins are created and deactivated. Administrators only.
          { id: 'commission', label: 'Commission Engine', icon: Percent, adminOnly: true },
          { id: 'commissionRuleOption', label: 'Commission Rule Option', icon: Percent, adminOnly: true },
          {
            id: 'users',
            label: 'Users & Agents (RBAC)',
            icon: Users,
            count: scopedUsers.length,
            adminOnly: true,
          },
        ].filter((item) => isAdminActor || !(item as any).adminOnly)
         .map((item) => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          return (
            <button
              key={item.id}
              onClick={() => {
                setActiveSection(item.id);
                setIsMobileSidebarOpen(false);
              }}
              className={`w-full flex items-center justify-between px-5 py-2.5 transition-colors cursor-pointer text-left text-sm ${
                isActive
                  ? 'bg-blue-600/10 text-blue-400 border-r-4 border-blue-500 font-medium'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon className="w-4 h-4 shrink-0" />
                <span>{item.label}</span>
              </div>
              {item.count !== undefined && (
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-medium ${
                    isActive ? 'bg-blue-600/30 text-blue-300' : 'bg-slate-800 text-slate-400'
                  }`}
                >
                  {item.count}
                </span>
              )}
            </button>
          );
        })}

        <div className="px-5 mt-4 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
          Financials &amp; Media
        </div>

        {[
          { id: 'recovery', label: 'Recovery Logs', icon: DollarSign, count: scopedRecoveries.length },
          { id: 'photos', label: 'Geo-Tagged Photos', icon: Camera, count: photos.length },
          { id: 'voice', label: 'Voice Notes Library', icon: Mic, count: voiceNotes.length },
          // Firestore Service Optimization menu removed at your request. The
          // read-cost work it was reporting on is now done in the app itself:
          // scoped queries, count queries and 25 rows a page.
          { id: 'reports', label: 'Executive Reports', icon: BarChart3 },
          // System Settings holds "Bulk Delete by Agent / Branch / All",
          // the backups, the commission rates and the balance repair.
          // Administrators only.
          { id: 'settings', label: 'System Settings', icon: Settings, adminOnly: true },
        ].filter((item) => isAdminActor || !(item as any).adminOnly)
         .map((item) => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          return (
            <button
              key={item.id}
              onClick={() => {
                setActiveSection(item.id);
                setIsMobileSidebarOpen(false);
              }}
              className={`w-full flex items-center justify-between px-5 py-2.5 transition-colors cursor-pointer text-left text-sm ${
                isActive
                  ? 'bg-blue-600/10 text-blue-400 border-r-4 border-blue-500 font-medium'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon className="w-4 h-4 shrink-0" />
                <span>{item.label}</span>
              </div>
              {item.count !== undefined && (
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-medium ${
                    isActive ? 'bg-blue-600/30 text-blue-300' : 'bg-slate-800 text-slate-400'
                  }`}
                >
                  {item.count}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Sidebar Footer User Info */}
      <div className="p-4 border-t border-slate-800 bg-slate-900/50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center border border-slate-600 font-bold text-white text-xs shrink-0">
            {currentUser.name
              .split(' ')
              .map((n) => n[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-white truncate">{currentUser.name}</p>
            <p className="text-[11px] text-slate-400 capitalize truncate">{currentUser.role} Account</p>
          </div>
          <button
            onClick={logout}
            title="Log out of session"
            className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-800/80 rounded-lg transition cursor-pointer shrink-0"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-[calc(100vh-53px)] w-full max-w-full bg-[#f8fafc] text-slate-800 font-sans overflow-hidden relative">
      {/* Mobile Sidebar Overlay Drawer */}
      {isMobileSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="fixed inset-0 bg-slate-950/80 backdrop-blur-xs transition-opacity"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
          <aside className="relative w-72 max-w-[80vw] bg-[#0f172a] text-slate-300 flex flex-col h-full z-10 shadow-2xl border-r border-slate-800 animate-in slide-in-from-left duration-200">
            {renderNavContent()}
          </aside>
        </div>
      )}

      {/* Desktop Sidebar Navigation */}
      <aside className="hidden lg:flex w-64 bg-[#0f172a] text-slate-300 flex-col shrink-0 border-r border-slate-800 h-full">
        {renderNavContent()}
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0 w-full max-w-full overflow-hidden bg-[#f8fafc]">
        {/* Top Header */}
        <header className="min-h-14 sm:h-16 bg-white border-b border-slate-200 flex flex-wrap items-center justify-between px-3 sm:px-6 py-2 shrink-0 gap-2 w-full max-w-full">
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="lg:hidden p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer"
              title="Open Navigation Menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h2 className="text-sm sm:text-base md:text-lg font-bold text-slate-800 capitalize truncate">
              {activeSection === 'commissionRuleOption' ? 'Commission Rule' : activeSection.replace('-', ' ')}
            </h2>
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] sm:text-xs font-semibold rounded-md flex items-center gap-1 shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              <span className="hidden xs:inline">Live Status</span>
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Bank selector. Shown only to someone who covers more than one
                bank -- for everyone else it would be a dropdown with a single
                entry, so it stays out of the way. */}
            {userBanks.length > 1 && (
              <select
                value={activeBank}
                onChange={(e) => setActiveBank(e.target.value)}
                title="Which bank you are working on"
                className="bg-slate-100 border-none rounded-full px-3 py-1.5 text-xs sm:text-sm font-bold text-slate-800 focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
              >
                <option value="">All my banks ({userBanks.length})</option>
                {userBanks.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            )}

            {/* ONE search box, not two.
                
                This header box used to write to a value that NOTHING read --
                you could type a customer's name, press Enter, and the screen
                would not change, which is the more likely reason searching
                here never seemed to work. Meanwhile the Loan Accounts screen
                had its own working search, so there were two boxes on that
                screen and the header one was squeezed into 128 pixels.
                
                It is now hidden on the Loan Accounts screen, where the filter
                bar owns search and has room for it. Everywhere else it jumps
                you to Loan Accounts with the term applied, which is what
                typing a customer name in a header is asking for. */}
            {activeSection !== 'accounts' && (
              <div className="relative">
                <input
                  type="text"
                  placeholder="Find a customer…"
                  value={searchInput}
                  onChange={(e) => onSearchInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') runHeaderSearch();
                  }}
                  className="pl-8 pr-3 py-1.5 bg-slate-100 border-none rounded-full text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 w-32 xs:w-44 sm:w-60 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={runHeaderSearch}
                  title="Search accounts"
                  className="absolute left-2.5 top-2 opacity-40 hover:opacity-90 text-xs sm:text-sm cursor-pointer"
                >
                  🔍
                </button>
              </div>
            )}

            <div className="flex items-center gap-1 sm:gap-2">
              {/* Backend Storage & Database Manager Button */}
              <button
                onClick={() => setIsBackendStorageModalOpen(true)}
                className="px-2.5 sm:px-3.5 py-1.5 sm:py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-900 border border-indigo-200 rounded-full text-[11px] sm:text-xs font-bold flex items-center gap-1 sm:gap-1.5 shadow-xs transition cursor-pointer"
                title="Manage Central Backend Database & Storage (/data/srms_master_store.json)"
              >
                <Database className="w-3.5 h-3.5 text-indigo-600" />
                <span className="hidden sm:inline">Backend Storage</span>
                <span className="sm:hidden">Storage</span>
              </button>

              {/* PTP Digest Notification & Batch Button */}
              <button
                onClick={() => setIsPTPDigestOpen(true)}
                className={`px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-full text-[11px] sm:text-xs font-bold flex items-center gap-1 sm:gap-1.5 shadow-xs transition cursor-pointer ${
                  ptpPreview.expiredCount > 0
                    ? 'bg-amber-600 hover:bg-amber-700 text-white shadow-amber-600/20'
                    : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-600/20'
                }`}
                title="Run PTP Digest & Firestore Real-Time Status Resolver"
              >
                <Zap className="w-3.5 h-3.5 text-amber-300 fill-amber-300" />
                <span className="hidden sm:inline">PTP Digest</span>
                {ptpPreview.expiredCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-red-600 text-white text-[9px] sm:text-[10px] font-black rounded-full ml-0.5 leading-none animate-pulse">
                    {ptpPreview.expiredCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => exportAllBackendData()}
                className="px-2.5 sm:px-3.5 py-1.5 sm:py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-full text-[11px] sm:text-xs font-bold flex items-center gap-1 sm:gap-1.5 shadow-xs transition cursor-pointer border border-slate-700"
                title="Download complete backend database (Accounts, Recoveries, PTPs, Visits, Users, Logs, Remarks) into multi-sheet Excel (.xlsx)"
              >
                <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" />
                <span className="hidden lg:inline">Export Backend (.xlsx)</span>
                <span className="lg:hidden">Export</span>
              </button>

              {currentUser.role === 'admin' && (
                <button
                  onClick={() => setIsOTSSettingsOpen(true)}
                  className="px-2.5 sm:px-3.5 py-1.5 sm:py-2 bg-purple-700 hover:bg-purple-800 text-white rounded-full text-[11px] sm:text-xs font-bold flex items-center gap-1 sm:gap-1.5 shadow-xs transition cursor-pointer"
                  title="Admin OTS Settings: Edit Bank-wise Last Dates and Scheme Calculation Slabs"
                >
                  <Percent className="w-3.5 h-3.5 text-purple-200" />
                  <span className="hidden md:inline">OTS Settings</span>
                  <span className="md:hidden">OTS</span>
                </button>
              )}

              <button
                onClick={() => setIsExcelModalOpen(true)}
                className="px-2.5 sm:px-3.5 py-1.5 sm:py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full text-[11px] sm:text-xs font-bold flex items-center gap-1 sm:gap-1.5 shadow-xs transition cursor-pointer"
                title="Upload Excel spreadsheet to allocate accounts to agents"
              >
                <Upload className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Upload Excel</span>
              </button>

              {/*
                THE "AI STRATEGY" BUTTON HAS BEEN REMOVED FROM THIS HEADER.

                It did nothing. It set `isAIOpen`, but the window it opens is
                written as `{selectedAccount && isAIOpen && <AIModal/>}` -- so
                with no customer chosen there is nothing to render and the
                press had no effect at all. No message, no error, nothing.

                The AI button ON A CUSTOMER ROW still works, because there a
                customer is selected. A button that silently does nothing is
                worse than no button: it teaches people the app is broken.

                Say what it should do when pressed from up here and it comes
                straight back.
              */}
            </div>
          </div>
        </header>

        {/* Scrollable Section Content */}
        <div className="p-3 sm:p-6 lg:p-8 flex-1 overflow-y-auto flex flex-col gap-6 w-full max-w-full">
          {/* 1. OVERVIEW DASHBOARD */}
          {activeSection === 'messages' && (
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <h3 className="font-bold text-sm text-slate-900 mb-1">Messages</h3>
                <p className="text-xs text-slate-500 mb-4">
                  Send instructions to agents — one, several, or all of them — and read what
                  has been sent to you.
                </p>
                <MessageCentre />
              </div>
            </div>
          )}

          {activeSection === 'dashboard' && (
            <div className="space-y-6">
              {/* Promises to pay due today, and any still pending from
                  before today. Computed from the PTPs already loaded, so it
                  costs no extra reads. */}
              <PTPDueTodayBanner ptps={ptps} onOpen={() => setActiveSection('ptp')} />

              {/* Why the dashboard is showing zeros.
                  Accounts are read only for a chosen scope, which keeps the
                  Firestore bill down -- but before this notice existed an
                  administrator signed in to a screen of zeros with nothing
                  saying so, and it read as lost data. */}
              {accounts.length === 0 && !accountsLoading && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-2.5">
                    <Database className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-xs">
                      <p className="font-bold text-amber-900">
                        No accounts loaded yet — nothing has been deleted.
                      </p>
                      <p className="text-amber-800 mt-0.5">
                        {pendingScopeReload
                          ? `Your last view was ${pendingScopeReload.label}${
                              pendingScopeReload.count
                                ? ` (${pendingScopeReload.count.toLocaleString('en-IN')} accounts)`
                                : ''
                            }. Press “Load them again” to open it.`
                          : 'Accounts are read a scope at a time so the database is not billed for the whole book on every sign-in. Choose a bank, branch or agent in Loan Accounts.'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {pendingScopeReload && (
                      <button
                        type="button"
                        onClick={() => void resumeLastScope()}
                        className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold transition cursor-pointer"
                      >
                        Load them again
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setActiveSection('accounts')}
                      className="px-3 py-1.5 rounded-lg bg-white border border-amber-300 hover:bg-amber-100 text-amber-900 text-xs font-bold transition cursor-pointer"
                    >
                      Open Loan Accounts
                    </button>
                  </div>
                </div>
              )}

              {/* Hierarchy & Multi-Dimension Filter Bar for Admin */}
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                      Executive Portfolio Dimensions
                    </span>
                    {isDashFilterActive && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 animate-in fade-in">
                        Filtered View Active ({filteredDashAccounts.length} accounts)
                      </span>
                    )}
                  </div>
                  {isDashFilterActive && (
                    <button
                      onClick={resetDashFilters}
                      className="text-xs font-semibold text-rose-600 hover:text-rose-700 flex items-center gap-1 hover:underline cursor-pointer"
                    >
                      <RotateCcw className="w-3 h-3" />
                      <span>Reset All Filters</span>
                    </button>
                  )}
                </div>

                {/*
                  BANK, ZONE AND REGION ARE ONLY OFFERED TO SOMEBODY WHO HAS
                  MORE THAN ONE OF THEM.

                  A recovery department user is tied to one bank and one zone,
                  and a branch manager to one branch. Showing them "All Banks
                  (3)" and "All Zonal Offices (3)" told them they were looking
                  at three banks when they should be looking at one -- and
                  invited them to pick a bank that is not theirs, which their
                  own account scope would then contradict.

                  They keep Agent and Branch, which are the two dimensions
                  they genuinely have several of within their own zone.
                */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 text-xs">
                  {/* Agent Wise */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Agent / User Wise</label>
                    <select
                      value={dashUserFilter}
                      onChange={(e) => setDashUserFilter(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="ALL">All Agents / Users ({scopedUsers.length})</option>
                      {scopedUsers.map((u) => (
                        <option key={`dash-u-${u.id}`} value={u.agentId || u.id}>
                          {u.name} {u.agentId ? `(${u.agentId})` : `(${u.role})`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Bank Wise -- organisation-wide roles only */}
                  {spansWholeOrganisation && (
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Bank Wise</label>
                    <select
                      value={dashBankFilter}
                      onChange={(e) => setDashBankFilter(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="ALL">All Banks ({allUniqueBanks.length})</option>
                      {allUniqueBanks.map((b) => (
                        <option key={`dash-bank-${b}`} value={b}>
                          {b}
                        </option>
                      ))}
                    </select>
                  </div>
                  )}

                  {/* Zone Wise -- organisation-wide roles only */}
                  {spansWholeOrganisation && (
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Zonal Office Wise</label>
                    <select
                      value={dashZoneFilter}
                      onChange={(e) => setDashZoneFilter(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="ALL">All Zonal Offices ({allUniqueZonalOffices.length})</option>
                      {allUniqueZonalOffices.map((z) => (
                        <option key={`dash-zone-${z}`} value={z}>
                          {z}
                        </option>
                      ))}
                    </select>
                  </div>
                  )}

                  {/* Regional Office removed from the hierarchy at your
                      instruction. The levels are now
                      Bank -> Zonal Office -> Branch. The filter is gone with
                      it; the stored field on old records is left alone so
                      nothing already saved is damaged. */}

                  {/* Branch Wise */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Branch Wise</label>
                    <select
                      value={dashBranchFilter}
                      onChange={(e) => setDashBranchFilter(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="ALL">All Branches ({allUniqueBranches.length})</option>
                      {allUniqueBranches.map((br) => (
                        <option key={`dash-br-${br}`} value={br}>
                          {br}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Settlement Month Wise */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1 flex items-center justify-between">
                      <span>Settlement Month</span>
                      <span className="text-[10px] text-blue-600 font-bold">Default: Current</span>
                    </label>
                    <select
                      value={dashMonthFilter}
                      onChange={(e) => setDashMonthFilter(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800 font-semibold focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value={currentMonthStr}>Current Month ({currentMonthStr})</option>
                      <option value="ALL">All Recorded Months</option>
                      {availableDashMonths
                        .filter((m) => m !== currentMonthStr)
                        .map((m) => (
                          <option key={`dash-m-${m}`} value={m}>
                            {m}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* 4 Professional KPI Metric Cards */}
              <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-slate-500 text-sm font-medium">
                      {dashMonthFilter === 'ALL' ? 'Total Recovery (All Time)' : `Recovery (${dashMonthFilter})`}
                    </p>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      {dashMonthFilter === currentMonthStr ? 'Current Month' : dashMonthFilter}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">{formatINR(totalRecoverySum)}</h3>
                  <p className="text-xs text-green-600 mt-2 font-medium">
                    {filteredDashRecoveries.length} verified recoveries logged
                  </p>
                </div>

                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden group">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-slate-500 text-sm font-medium">
                      {isDashFilterActive ? 'Filtered PTP' : 'Total PTP (Current)'}
                    </p>
                    <button
                      onClick={() => setIsPTPDigestOpen(true)}
                      className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2 py-0.5 rounded-md border border-indigo-200 flex items-center gap-1 transition cursor-pointer"
                      title="Run PTP Digest"
                    >
                      <Zap className="w-3 h-3 text-amber-500 fill-amber-500" />
                      <span>Digest</span>
                    </button>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">
                    {filteredDashPtps.length} Commitments
                  </h3>
                  <div className="flex items-center justify-between mt-2 text-xs">
                    <span className="text-blue-600 font-medium">
                      {filteredDashPtps.filter((p) => p.status === 'Achieved').length} Achieved • {filteredDashPtps.filter((p) => p.status === 'Pending').length} Pending
                    </span>
                    {ptpPreview.expiredCount > 0 && (
                      <span className="text-red-600 font-bold bg-red-50 px-1.5 py-0.5 rounded text-[10px] border border-red-200 animate-pulse">
                        {ptpPreview.expiredCount} Expired
                      </span>
                    )}
                  </div>
                </div>

                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <p className="text-slate-500 text-sm font-medium mb-1">
                    {isDashFilterActive ? 'Filtered Overdue' : 'Overdue Portfolio'}
                  </p>
                  <h3 className="text-2xl font-bold text-red-600">{formatINR(totalOverdueSum)}</h3>
                  <p className="text-xs text-red-500 mt-2 font-medium">
                    Across {filteredDashAccounts.length} loan accounts
                  </p>
                </div>

                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <p className="text-slate-500 text-sm font-medium mb-1">Field Agents Active</p>
                  <h3 className="text-2xl font-bold text-slate-900">
                    {activeAgents.length} / {scopedUsers.filter((u) => u.role === 'agent').length}
                  </h3>
                  <p className="text-xs text-slate-500 mt-2 font-medium">{filteredDashVisits.length} Geo-visits logged</p>
                </div>
              </section>

              {/* Grid: High-Value Allocations & Geo-Verification / Commission */}
              <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0">
                {/* Left 8 cols: Recent Allocations & Agent Performance */}
                <div className="lg:col-span-8 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
                  <div className="p-5 border-b border-slate-100 flex justify-between items-center">
                    <h4 className="font-bold text-slate-800">Recent High-Value Loan Accounts</h4>
                    <button
                      onClick={() => setActiveSection('accounts')}
                      className="text-sm text-blue-600 hover:text-blue-700 font-semibold cursor-pointer"
                    >
                      View All ({accounts.length})
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-slate-50 text-slate-500 text-xs uppercase sticky top-0 border-b border-slate-100">
                        <tr>
                          <th className="px-6 py-3 font-semibold">Account ID</th>
                          <th className="px-6 py-3 font-semibold">Customer</th>
                          <th className="px-6 py-3 font-semibold">Overdue</th>
                          <th className="px-6 py-3 font-semibold">Agent</th>
                          <th className="px-6 py-3 font-semibold">Status</th>
                          <th className="px-6 py-3 font-semibold text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm divide-y divide-slate-100 text-slate-700">
                        {accounts.slice(0, 5).map((acc, idx) => (
                          <tr key={acc.id || acc.accountId || `acc-dash-${idx}`} className="hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 font-mono text-xs font-semibold text-blue-600">
                              {acc.accountId}
                            </td>
                            <td className="px-6 py-4 font-medium text-slate-900">
                              {acc.customerName}
                              <span className="block text-xs text-slate-400 font-normal">{productOf(acc)}</span>
                            </td>
                            <td className="px-6 py-4 text-red-600 font-semibold font-mono">
                              {formatINR(acc.overdueAmount)}
                            </td>
                            <td className="px-6 py-4 text-slate-700 text-xs">
                              {acc.assignedAgentName}
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                                  acc.accountStatus === 'Recovered'
                                    ? 'bg-green-100 text-green-700'
                                    : acc.accountStatus === 'PTP Pending'
                                    ? 'bg-blue-100 text-blue-700'
                                    : acc.accountStatus === 'Field Visit Pending'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {acc.accountStatus}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() => openAccountModal(acc, 'timeline')}
                                className="text-xs text-blue-600 hover:text-blue-800 font-semibold cursor-pointer"
                              >
                                View Timeline →
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Agent Target Progress Bars */}
                  <div className="p-5 border-t border-slate-100 bg-slate-50/50 space-y-3">
                    <h5 className="text-xs font-bold uppercase tracking-wider text-slate-600">
                      Field Agent Target Achievement ({new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })})
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {activeAgents.slice(0, 3).map((agent, idx) => {
                        const agentRecs = recoveries.filter(
                          (r) => r.agentId === agent.agentId || r.agentId === agent.id
                        );
                        const recovered = agentRecs.reduce((sum, r) => sum + r.amount, 0);
                        const target = agent.monthlyTarget || 300000;
                        const pct = Math.min(100, Math.round((recovered / target) * 100));

                        return (
                          <div key={agent.id || agent.agentId || `ag-target-${idx}`} className="bg-white p-3.5 rounded-xl border border-slate-200 space-y-1.5">
                            <div className="flex justify-between items-center text-xs">
                              <span className="font-bold text-slate-900">{agent.name}</span>
                              <span className="text-blue-600 font-semibold">{pct}%</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                              <div
                                className="bg-blue-600 h-full rounded-full transition-all"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <div className="flex justify-between text-[11px] text-slate-500">
                              <span>{formatINR(recovered)}</span>
                              <span>Target: {formatINR(target)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Right 4 cols: Geo-Tagged Verification & Commission Payouts */}
                <div className="lg:col-span-4 flex flex-col gap-6">
                  {/* Geo-Tagged Verification Feed */}
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-bold text-slate-800">Geo-Tagged Verification</h4>
                      <button
                        onClick={() => setActiveSection('photos')}
                        className="text-xs text-blue-600 font-semibold hover:underline"
                      >
                        All Photos ({photos.length})
                      </button>
                    </div>

                    <div className="space-y-4 overflow-y-auto max-h-80">
                      {photos.slice(0, 2).map((ph, idx) => (
                        <div
                          key={ph.id || `photo-preview-${idx}`}
                          className={`flex gap-3 pl-3 ${
                            idx === 0 ? 'border-l-2 border-blue-500' : 'border-l-2 border-slate-200 opacity-80'
                          }`}
                        >
                          <div className="flex-1 space-y-1">
                            <p className="text-xs font-bold text-slate-900">
                              Visit Verified: {ph.accountId}
                            </p>
                            <p className="text-[10px] text-slate-500">
                              Agent: {ph.agentId} • {ph.time}
                            </p>
                            <div className="mt-2 w-full h-24 bg-slate-100 rounded-lg flex items-center justify-center border border-slate-200 relative overflow-hidden">
                              <img
                                src={(ph.downloadUrl || ph.dataUrl)}
                        title="Click to open the full-size photo"
                        onClick={() => {
                          const u = ph.downloadUrl || ph.dataUrl;
                          if (u) window.open(u, "_blank", "noopener");
                        }}
                                alt="Geo Stamp"
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-2">
                                <span className="text-[9px] text-white font-mono font-medium">
                                  Lat: {typeof ph.latitude === 'number' ? ph.latitude.toFixed(4) : '—'} | Lon: {typeof ph.longitude === 'number' ? ph.longitude.toFixed(4) : '—'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Commission Payouts Card - Deep Blue Rich Accent */}
                  <div className="bg-blue-900 rounded-xl p-5 text-white flex flex-col justify-between relative overflow-hidden shadow-sm">
                    <div className="relative z-10">
                      {/* The rate used to be printed as a fixed "(10%)" while
                          the line underneath showed the real configured rate.
                          Two different numbers on one card, one of them
                          invented. The heading no longer claims a rate. */}
                      <h4 className="font-bold text-sm mb-1 text-blue-100">Commission Payouts</h4>
                      <p className="text-2xl font-bold">{formatINR(totalCommissionSum)}</p>
                      <p className="text-xs text-blue-200 mt-1">
                        Projected for {new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })} cycle • {commissionSettings.defaultRate}% Base
                      </p>
                    </div>
                    <div className="mt-5 flex gap-2 relative z-10">
                      <button
                        onClick={() => setActiveSection('commission')}
                        className="flex-1 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-xs font-bold text-white transition shadow-sm"
                      >
                        {/* This button approves nothing -- it opens the
                            Commission screen, where approving is done one
                            record at a time. It now says so. */}
                        Open Commission
                      </button>
                      <button
                        onClick={() => setActiveSection('reports')}
                        className="flex-1 py-2 bg-white/15 hover:bg-white/25 rounded-lg text-xs font-bold text-white transition"
                      >
                        Reports
                      </button>
                    </div>
                    <div className="absolute -right-10 -bottom-10 w-32 h-32 bg-blue-500/20 rounded-full pointer-events-none" />
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* 2. CUSTOMER ACCOUNTS MASTER TABLE */}
          {activeSection === 'accounts' && (
            <div className="space-y-4">
              {/* Choose before reading. Administrators only -- an agent's
                  own allotment loads by itself, because it is small and it is
                  all they can see. */}
              {(currentUser.role === 'admin' || currentUser.role === 'management') && (
                <AccountsScopeBar />
              )}

              {/* Sorting, shortlisting and paging, in one bar, where the
                  accounts are. The old layout forced a search box and five
                  dropdowns onto one line, which squeezed the search to a
                  sliver and overlapped Account Status with Customer Rating. */}
              {(accountsScope.kind !== 'none' || accounts.length > 0) && (
                <AccountsFilterBar
                  value={acctFilters}
                  onChange={setAcctFilters}
                  onApply={setAcctFilters}
                  page={pagedAccounts.page}
                  pageCount={pagedAccounts.pageCount}
                  total={pagedAccounts.total}
                  from={pagedAccounts.from}
                  to={pagedAccounts.to}
                  noun="customers"
                  onPage={setAcctPage}
                  statusOptions={[
                    'Active',
                    'PTP Broken',
                    'Disputed',
                    'Legal Action',
                    'Field Visit Needed',
                    'Untraceable',
                    'OTS Processing',
                    'OTS Settled – Customer Account Closed',
                    'Closed as per OTS',
                    'Regular Close',
                    'Closed',
                  ]}
                  zoneOptions={allUniqueZones}
                  branchOptions={allUniqueBranches}
                  productOptions={productFilterOptions}
                  accountCategoryOptions={accountCategoryFilterOptions}
                  agentOptions={users
                    .filter((u) => u.role === 'agent')
                    .map((u) => ({ id: u.agentId || u.id, name: `${u.name} (${u.agentId || u.id})` }))}
                  showAgentFilter
                />
              )}

              {/* Nothing chosen yet: say so rather than showing an empty
                  table that reads as "no accounts exist". */}
              {accountsScope.kind === 'none' && accounts.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center">
                  <Database className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-bold text-slate-900 text-sm">No accounts loaded yet</h3>
                  <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                    Choose a bank, branch or agent above and press “Open these
                    accounts”. Nothing is read from the database until you do —
                    that is what keeps the bill down.
                  </p>
                </div>
              ) : (
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col gap-3 text-xs">
                {/* Action row. Also wraps -- the count used to sit on top of
                    the buttons on a narrower window. */}
                <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100">
                  <span className="text-slate-500 text-xs font-medium mr-2 shrink-0">
                    Showing <span className="text-slate-900 font-bold">{filteredAccounts.length}</span> of{' '}
                    {accounts.length} accounts
                  </span>

                  <button
                    onClick={() => exportAllBackendData()}
                    className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs transition cursor-pointer border border-slate-700"
                    title="Export complete backend database (Accounts, Recoveries, PTPs, Visits, Users, Logs) to Excel (.xlsx)"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Export All Backend (.xlsx)</span>
                  </button>

                  <button
                    onClick={() => setIsExcelModalOpen(true)}
                    className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs transition cursor-pointer"
                    title="Upload Excel or CSV to allocate accounts to agents"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    <span>Upload Excel / Bulk Allocate</span>
                  </button>

                  <button
                    onClick={downloadExactExcelTemplate}
                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold flex items-center gap-1.5 border border-slate-200 transition cursor-pointer"
                    title="Download Excel template matching bank format with AGENT_ID"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Template</span>
                  </button>

                  {(currentUser.role === 'admin') && (
                    <button
                      onClick={() => setIsCategoryModalOpen(true)}
                      className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-800 rounded-xl text-xs font-semibold flex items-center gap-1.5 border border-indigo-200 transition cursor-pointer"
                      title="Set the account category (SMA 0, SMA 1, SMA 2, NPA) on existing accounts without re-uploading"
                    >
                      <span>Set account category</span>
                    </button>
                  )}

                  {/* Bulk Delete / Purge Action Controls */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openBulkDeleteModal('agent')}
                      className="px-3 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition cursor-pointer shadow-xs"
                      title="Clear or delete bulk accounts by Agent, Branch, or All"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Bulk Delete Accounts</span>
                    </button>

                    <div className="hidden sm:flex items-center bg-rose-50 border border-rose-200 rounded-xl p-0.5">
                      <button
                        onClick={() => openBulkDeleteModal('agent')}
                        className="px-2 py-1.5 hover:bg-rose-100 text-rose-800 rounded-lg text-[11px] font-semibold transition cursor-pointer"
                        title="Delete accounts assigned to a specific agent"
                      >
                        By Agent
                      </button>
                      <button
                        onClick={() => openBulkDeleteModal('branch')}
                        className="px-2 py-1.5 hover:bg-rose-100 text-rose-800 rounded-lg text-[11px] font-semibold transition cursor-pointer"
                        title="Delete accounts under a specific branch"
                      >
                        By Branch
                      </button>
                      <button
                        onClick={() => openBulkDeleteModal('all')}
                        className="px-2 py-1.5 hover:bg-rose-100 text-rose-800 rounded-lg text-[11px] font-semibold transition cursor-pointer"
                        title="Permanently wipe all accounts from storage"
                      >
                        All
                      </button>
                    </div>
                  </div>
                </div>
                </div>
              )}

              {/* Batch Actions Toolbar when accounts are selected */}
              {selectedAccountIds.length > 0 && (
                <div className="bg-slate-900 text-white px-4 py-3 rounded-xl flex flex-wrap items-center justify-between gap-3 shadow-lg border border-slate-800 animate-in fade-in slide-in-from-top-2">
                  <div className="flex items-center gap-3">
                    <span className="bg-rose-600 text-white font-mono text-xs px-2.5 py-1 rounded-full font-bold">
                      {selectedAccountIds.length} Account{selectedAccountIds.length > 1 ? 's' : ''} Selected
                    </span>
                    <span className="text-xs text-slate-300 font-medium">Bulk Action Options:</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openBulkDeleteModal('selected')}
                      className="px-3.5 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition cursor-pointer shadow-xs"
                      title="Delete all currently selected accounts"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Delete Selected ({selectedAccountIds.length})</span>
                    </button>

                    <button
                      onClick={() => setSelectedAccountIds([])}
                      className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-medium transition cursor-pointer"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>
              )}

              {/* Master Accounts Table */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200 uppercase tracking-wider">
                      <tr>
                        <th className="p-3.5 w-10 text-center">
                          <input
                            type="checkbox"
                            checked={
                              filteredAccounts.length > 0 &&
                              filteredAccounts.every((a) => selectedAccountIds.includes(a.accountId))
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                const currentVisibleIds = filteredAccounts.map((a) => a.accountId);
                                setSelectedAccountIds((prev) => Array.from(new Set([...prev, ...currentVisibleIds])));
                              } else {
                                const currentVisibleIds = new Set(filteredAccounts.map((a) => a.accountId));
                                setSelectedAccountIds((prev) => prev.filter((id) => !currentVisibleIds.has(id)));
                              }
                            }}
                            className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                            title="Select/Deselect all visible accounts"
                          />
                        </th>
                        <th className="p-3.5">Account ID</th>
                        <th className="p-3.5">Customer Details</th>
                        <th className="p-3.5">Product &amp; Branch</th>
                        <th className="p-3.5">Overdue / Total</th>
                        <th className="p-3.5">PTP Count / Risk</th>
                        <th className="p-3.5">Assigned Agent</th>
                        <th className="p-3.5">Status</th>
                        <th className="p-3.5 text-right">Quick Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-700">
                      {filteredAccounts.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="p-10 text-center text-slate-500">
                            <div className="flex flex-col items-center justify-center gap-2 max-w-sm mx-auto">
                              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                                <Users className="w-6 h-6" />
                              </div>
                              <p className="font-semibold text-slate-800 text-sm">
                                {accounts.length === 0
                                  ? 'No accounts loaded'
                                  : 'Nothing matches this shortlist'}
                              </p>
                              <p className="text-xs text-slate-500 leading-relaxed">
                                {accounts.length === 0
                                  ? 'Choose a bank, branch or agent above and press “Open these accounts”, or upload your Excel master recovery sheet.'
                                  : `${accounts.length.toLocaleString('en-IN')} accounts are loaded, but none of them match what you have asked for. Widen the amount range, clear the rating, or press “Clear all”.`}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        // 25 rows at a time. The whole matching set is still what select-all and
                        // the totals above use -- only the drawing is paged.
                        pagedAccounts.rows.map((group, idx) => {
                        const acc = group.primary;
                        const manyLoans = group.loanCount > 1;
                        // The row is the BORROWER, so the tick covers every loan behind it.
                        const groupIds = group.loans.map((l) => l.accountId);
                        const rowSelected = groupIds.every((id) => selectedAccountIds.includes(id));
                        return (
                        <tr
                          key={acc.id || acc.accountId || `acc-master-${idx}`}
                          className={`hover:bg-slate-50 transition-colors ${
                            rowSelected ? 'bg-rose-50/40' : ''
                          }`}
                        >
                          <td className="p-3.5 text-center">
                            <input
                              type="checkbox"
                              checked={rowSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedAccountIds((prev) =>
                                    Array.from(new Set([...prev, ...groupIds]))
                                  );
                                } else {
                                  setSelectedAccountIds((prev) =>
                                    prev.filter((id) => !groupIds.includes(id))
                                  );
                                }
                              }}
                              className="rounded border-slate-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                              title={
                                manyLoans
                                  ? `Selects all ${group.loanCount} loans of this borrower`
                                  : undefined
                              }
                            />
                          </td>
                          <td className="p-3.5 font-mono font-bold text-blue-600">
                            <div>{acc.accountId}</div>
                            {manyLoans && (
                              <button
                                onClick={() => openAccountModal(acc, 'singleView')}
                                className="mt-1 inline-flex items-center gap-1 text-[10px] font-extrabold bg-purple-100 hover:bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full border border-purple-300 transition cursor-pointer"
                                title="Click to open Consolidated Single Customer View"
                              >
                                <Layers className="w-2.5 h-2.5" />
                                <span>Multi-Acc ({group.loanCount})</span>
                              </button>
                            )}
                          </td>
                          <td className="p-3.5">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-900">{acc.customerName}</span>
                              <button
                                onClick={() => openAccountModal(acc, 'singleView')}
                                className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-1.5 py-0.5 rounded border border-indigo-200 transition cursor-pointer"
                                title="Open Single Customer View with all linked loans & notes"
                              >
                                Single View
                              </button>
                            </div>
                            <div className="flex items-center gap-1.5 mt-1">
                              <StarRating
                                rating={acc.customerStarRating || 0}
                                onChange={(newRating) => updateCustomerStarRating(acc.accountId, newRating)}
                                size="sm"
                                showPrimeBadge
                              />
                            </div>
                            <div className="text-slate-500 text-[11px] font-mono mt-0.5">{acc.mobile}</div>
                            <div className="text-slate-400 text-[10px] truncate max-w-[220px]">{acc.address}</div>
                            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                              {acc.branch && (
                                <span className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded font-medium">
                                  {acc.branch}
                                </span>
                              )}
                              {acc.zone && (
                                <span className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-100 px-1.5 py-0.5 rounded font-semibold">
                                  {acc.zone}
                                </span>
                              )}
                            </div>
                            {acc.agentNotesHistory && acc.agentNotesHistory.length > 0 && (
                              <div className="mt-1 flex items-center gap-1 text-[10px] text-indigo-700 bg-indigo-50/80 px-2 py-0.5 rounded max-w-[240px] truncate border border-indigo-100">
                                <MessageSquare className="w-2.5 h-2.5 shrink-0" />
                                <span className="font-medium truncate"><span className="text-slate-400 font-bold">AGENT NOTE:</span> {latestAgentNote(acc.agentNotesHistory)?.note || '—'}</span>
                              </div>
                            )}
                          </td>
                          {/* "Product & Branch", and now actually product and
                              branch. The column has always been headed that
                              way, but the admin list put the NPA date on the
                              second line while the agent list put the branch
                              there -- so the same column, under the same
                              heading, read differently depending on who was
                              logged in. The branch is what the heading
                              promises and what the agent already sees; the NPA
                              date follows it in smaller type rather than being
                              dropped, since nothing else in this row carries
                              it. */}
                          <td className="p-3.5">
                            <span className="text-slate-700 font-medium">{productOf(acc)}</span>
                            <div className="text-[10px] text-slate-400">{acc.branch}</div>
                            {acc.npaDate && (
                              <div className="text-[10px] text-slate-400 font-mono">NPA: {displayDate(acc.npaDate)}</div>
                            )}
                          </td>
                          <td className="p-3.5 font-mono">
                            {/* The borrower's WHOLE exposure, added across their loans. */}
                            <div className="text-red-600 font-bold">{formatINR(group.totalOverdue)}</div>
                            <div className="text-slate-400 text-[10px]">Total: {formatINR(group.totalOutstanding)}</div>
                            {manyLoans && (
                              <div className="text-[10px] text-slate-400">across {group.loanCount} loans</div>
                            )}
                          </td>
                          <td className="p-3.5">
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                                (acc.ptpCount ?? 0) >= 3
                                  ? 'bg-rose-50 text-rose-700 border-rose-200'
                                  : (acc.ptpCount ?? 0) >= 1
                                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                                  : 'bg-slate-50 text-slate-700 border-slate-200'
                              }`}
                            >
                              {acc.ptpCount ?? 0} PTPs
                            </span>
                            <div className="text-[10px] text-slate-500 mt-0.5">{acc.customerCategory}</div>
                          </td>
                          <td className="p-3.5">
                            <div className="font-semibold text-slate-800">{acc.assignedAgentName}</div>
                            <div className="text-[10px] font-mono text-blue-600">{acc.assignedAgentId}</div>
                          </td>
                          <td className="p-3.5">
                            <span
                              className={`text-[10px] font-bold px-2.5 py-1 rounded-md border ${
                                acc.accountStatus === 'Closed as per OTS'
                                  ? 'bg-purple-100 text-purple-800 border-purple-300 shadow-xs'
                                  : acc.accountStatus === 'Regular Close'
                                  ? 'bg-emerald-100 text-emerald-800 border-emerald-300 shadow-xs'
                                  : acc.accountStatus === 'Recovered'
                                  ? 'bg-green-100 text-green-700 border-green-200'
                                  : acc.accountStatus === 'PTP Pending'
                                  ? 'bg-blue-100 text-blue-700 border-blue-200'
                                  : acc.accountStatus === 'Field Visit Pending'
                                  ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                                  : 'bg-slate-100 text-slate-700 border-slate-200'
                              }`}
                            >
                              {acc.accountStatus}
                            </span>
                          </td>
                          <td className="p-3.5 text-right space-x-1 whitespace-nowrap">
                            <button
                              onClick={() => openAccountModal(acc, 'singleView')}
                              className="px-2 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-bold border border-indigo-200 transition cursor-pointer"
                              title="Consolidated Customer View"
                            >
                              <Layers className="w-3.5 h-3.5 inline mr-0.5" />
                              View
                            </button>
                            <button
                              onClick={() => openAccountModal(acc, 'note')}
                              className="p-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg border border-indigo-200 transition cursor-pointer"
                              title="Add Agent Note"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => openAccountModal(acc, 'whatsapp')}
                              className="p-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg border border-emerald-200 transition cursor-pointer"
                              title="WhatsApp Notification & Draft Studio (Payment Reminder, Lok Adalat, OTS Offer)"
                            >
                              <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />
                            </button>
                            <button
                              onClick={() => openAccountModal(acc, 'timeline')}
                              className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold border border-slate-200 transition cursor-pointer"
                              title="View Account Timeline"
                            >
                              Timeline
                            </button>
                            <button
                              onClick={() => openAccountModal(acc, 'watermark')}
                              className="p-1.5 bg-sky-50 hover:bg-sky-100 text-sky-700 rounded-lg border border-sky-200 transition cursor-pointer"
                              title="Geo-Tagged Watermark Camera"
                            >
                              <Camera className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => openAccountModal(acc, 'voice')}
                              className="p-1.5 bg-pink-50 hover:bg-pink-100 text-pink-700 rounded-lg border border-pink-200 transition cursor-pointer"
                              title="Voice Note"
                            >
                              <Mic className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => openAccountModal(acc, 'recovery')}
                              className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition shadow-xs cursor-pointer"
                              title="Collect Payment"
                            >
                              + ₹
                            </button>
                            <button
                              onClick={() => openAccountModal(acc, 'ai')}
                              className="p-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg border border-blue-200 transition cursor-pointer"
                              title="AI Strategy"
                            >
                              <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                            </button>
                            <button
                              onClick={() => openCloseAccountModal(acc)}
                              className="p-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg border border-rose-200 transition cursor-pointer"
                              title="Close Account (Closed as per OTS / Regular Close / Delete)"
                            >
                              <Archive className="w-3.5 h-3.5" />
                            </button>
                            {currentUser.role === 'admin' && (
                              <button
                                onClick={() => openDeleteAccountModal(acc)}
                                className="p-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg border border-rose-200 transition cursor-pointer"
                                title="Permanent Delete Account"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                        );
                      }))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* 3. ACCOUNT ALLOCATION (SINGLE & BULK & EXCEL) */}
          {activeSection === 'allocation' && (
            <div className="space-y-6">
              {/* Excel Upload Hero Card with Standard Format columns */}
              <div className="bg-gradient-to-r from-blue-900 to-slate-900 text-white rounded-2xl p-6 shadow-md border border-slate-800 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="bg-white/95 px-2.5 py-1 rounded-lg">
                        <ScaleSupportLogo variant="compact" size="sm" showTagline={false} />
                      </div>
                      <span className="text-xs font-bold text-blue-300 uppercase tracking-wider">
                        Excel Account Allocation Center
                      </span>
                    </div>
                    <h3 className="text-xl font-black text-white">
                      Import Bank Excel Allocation Sheets
                    </h3>
                    <p className="text-xs text-slate-300 max-w-2xl">
                      Upload accounts using your standard bank format (<code className="font-mono text-blue-200">BRANCH_COD, BRANCH_NAME, ACCT_NO, NAME, PROD_DESC, FACILITY, LIMIT_SANCTI, LOAN_B, NPA_DT, CONTACT NO., CUSTOMER, ADDRESS</code>) and distribute directly to field recovery agents.
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={downloadExactExcelTemplate}
                      className="px-4 py-2.5 bg-slate-800/90 hover:bg-slate-700 text-blue-300 rounded-xl text-xs font-bold border border-slate-700 flex items-center gap-2 transition cursor-pointer shadow-sm"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download Sample Excel Template</span>
                    </button>

                    <button
                      onClick={() => setIsExcelModalOpen(true)}
                      className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-black flex items-center gap-2 transition shadow-md cursor-pointer"
                    >
                      <Upload className="w-4 h-4" />
                      <span>Upload &amp; Allocate Excel (.xlsx)</span>
                    </button>
                  </div>
                </div>

                {/* Column schema pills */}
                <div className="pt-2 border-t border-slate-800/80 flex flex-wrap items-center gap-1.5 text-[11px]">
                  <span className="text-slate-400 font-semibold mr-1">Supported Format:</span>
                  {[
                    'BRANCH_COD',
                    'BRANCH_NAME',
                    'ACCT_NO',
                    'NAME',
                    'PROD_DESC',
                    'FACILITY',
                    'LIMIT_SANCTI',
                    'LOAN_B',
                    'NPA_DT',
                    'CONTACT NO.',
                    'CUSTOMER',
                    'ADDRESS',
                  ].map((col, idx) => (
                    <span
                      key={`tmpl-col-${col}-${idx}`}
                      className="px-2 py-0.5 bg-slate-800/90 text-blue-200 font-mono text-[10px] font-bold rounded border border-slate-700/80"
                    >
                      {col}
                    </span>
                  ))}
                </div>
              </div>

              {/* Bulk Allocation Action Box */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-bold text-sm text-slate-900">
                      Bulk Account Allocation &amp; Reassignment
                    </h3>
                    <p className="text-xs text-slate-500">
                      Select multiple overdue accounts from the list below and assign directly to field recovery agents
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      value={selectedAgentForAllocation}
                      onChange={(e) => setSelectedAgentForAllocation(e.target.value)}
                      className="bg-slate-50 text-slate-800 text-xs border border-slate-200 rounded-xl px-3 py-2 font-medium focus:bg-white focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    >
                      {/* An explicit empty choice, so the box never DISPLAYS an
                          agent it is not actually holding. That mismatch is
                          what sent 412 accounts to the administrator. */}
                      <option value="">Assign to: choose an agent…</option>
                      {activeAgents.length > 0 ? (
                        activeAgents.map((ag, idx) => (
                          <option key={`ag-alloc-opt-${ag.id || ag.agentId || idx}`} value={ag.agentId || ag.id}>
                            Assign to: {ag.name} ({ag.agentId || 'RA'})
                          </option>
                        ))
                      ) : (
                        users.map((u, idx) => (
                          <option key={`u-alloc-opt-${u.id || u.agentId || idx}`} value={u.agentId || u.id}>
                            Assign to: {u.name} ({u.agentId || u.role})
                          </option>
                        ))
                      )}
                    </select>

                    <button
                      onClick={handleBulkAllocate}
                      disabled={selectedAccountIds.length === 0}
                      className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5 ${
                        selectedAccountIds.length > 0
                          ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm cursor-pointer'
                          : 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                      }`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Allocate Selected ({selectedAccountIds.length})</span>
                    </button>
                  </div>
                </div>

                {/* Does the agent actually see them?
                    An allocation that looks done on this screen but leaves the
                    agent's list empty is the hardest thing in the app to
                    diagnose, because both screens are telling the truth about
                    different things: this one reads the local copy, the agent's
                    reads the database by agent ID. This asks the database the
                    same question the agent's login asks -- one count query,
                    billed as a single read however many accounts it counts. */}
                {selectedAgentForAllocation && (
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <button
                      type="button"
                      onClick={async () => {
                        const ag = users.find(
                          (u) =>
                            (u.agentId && u.agentId === selectedAgentForAllocation) ||
                            u.id === selectedAgentForAllocation
                        );
                        if (!ag) {
                          setAllocCheck('That agent could not be found in the user list.');
                          return;
                        }
                        const key = ag.agentId || ag.id;
                        setAllocCheck('Checking the database…');
                        try {
                          const n = await countAccountsFor({ agentId: key });
                          setAllocCheck(
                            `The database holds ${n.toLocaleString(
                              'en-IN'
                            )} account(s) stamped with agent ID "${key}". This is exactly what ${
                              ag.name
                            }'s own login reads, so their list will show ${n.toLocaleString(
                              'en-IN'
                            )}.` +
                              (n === 0
                                ? ' If you have just allocated accounts to them, the allocation did not reach the database — send this message on and it will be fixed.'
                                : '')
                          );
                        } catch (err: any) {
                          setAllocCheck(`Could not check: ${err?.message || 'unknown error'}`);
                        }
                      }}
                      className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 font-bold text-slate-700 transition cursor-pointer"
                    >
                      Check what this agent can see
                    </button>
                    {allocCheck && (
                      <span className="text-slate-700 font-semibold">{allocCheck}</span>
                    )}
                  </div>
                )}

                {/* Allocation filters -- your line 46. Bank, several branches
                    and several current assignees at once, so "move everything
                    these two agents hold in these three branches" is one
                    action rather than a hunt. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs">
                  <div>
                    <label className="block text-slate-600 font-bold mb-1">
                      1. Bank{allocBanks.length > 1 ? ` (${allocBanks.length})` : ''}
                    </label>
                    <div className="max-h-24 overflow-y-auto bg-white border border-slate-200 rounded-xl p-2 space-y-1">
                      {allUniqueBanks.length === 0 ? (
                        <span className="text-slate-400">No banks.</span>
                      ) : (
                        allUniqueBanks.map((b, i) => (
                          <label key={`af-bank-${b || i}`} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={allocBanks.includes(b)}
                              onChange={(e) =>
                                setAllocBanks((prev) =>
                                  e.target.checked ? [...prev, b] : prev.filter((x) => x !== b)
                                )
                              }
                            />
                            <span className="text-slate-800 truncate">{b}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  {/* 2. Zonal office, narrowed to the ticked banks. */}
                  <div>
                    <label className="block text-slate-600 font-bold mb-1">
                      2. Zonal Office{allocZones.length > 1 ? ` (${allocZones.length})` : ''}
                    </label>
                    <div className="max-h-24 overflow-y-auto bg-white border border-slate-200 rounded-xl p-2 space-y-1">
                      {allocZoneOptions.length === 0 ? (
                        <span className="text-slate-400">
                          {allocBanks.length
                            ? 'No zonal office recorded under the bank you ticked.'
                            : 'No zonal offices.'}
                        </span>
                      ) : (
                        allocZoneOptions.map((z, i) => (
                          <label key={`af-zone-${z || i}`} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={allocZones.includes(z)}
                              onChange={(e) =>
                                setAllocZones((prev) =>
                                  e.target.checked ? [...prev, z] : prev.filter((x) => x !== z)
                                )
                              }
                            />
                            <span className="text-slate-800 truncate">{z}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-600 font-bold mb-1">
                      3. Branch{allocBranches.length > 1 ? `es (${allocBranches.length})` : ''}
                    </label>
                    <div className="max-h-24 overflow-y-auto bg-white border border-slate-200 rounded-xl p-2 space-y-1">
                      {allocBranchOptions.length === 0 ? (
                        <span className="text-slate-400">
                          {allocBanks.length || allocZones.length
                            ? 'No branch recorded under what you ticked.'
                            : 'No branches.'}
                        </span>
                      ) : (
                        allocBranchOptions.map((b, i) => (
                          <label key={`af-br-${b || i}`} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={allocBranches.includes(b)}
                              onChange={(e) =>
                                setAllocBranches((prev) =>
                                  e.target.checked ? [...prev, b] : prev.filter((x) => x !== b)
                                )
                              }
                            />
                            <span className="text-slate-800 truncate">{b}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-600 font-bold mb-1">
                      4. Currently held by{allocAgents.length > 1 ? ` (${allocAgents.length})` : ''}
                      <span className="font-normal text-slate-400"> (optional)</span>
                    </label>
                    <div className="max-h-24 overflow-y-auto bg-white border border-slate-200 rounded-xl p-2 space-y-1">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allocAgents.includes('unassigned')}
                          onChange={(e) =>
                            setAllocAgents((prev) =>
                              e.target.checked
                                ? [...prev, 'unassigned']
                                : prev.filter((x) => x !== 'unassigned')
                            )
                          }
                        />
                        <span className="text-slate-800 font-semibold">Unassigned</span>
                      </label>
                      {allocAgentOptions.length === 0 && (
                        <span className="text-slate-400">
                          No agent recorded under what you ticked.
                        </span>
                      )}
                      {allocAgentOptions.map((u, i) => {
                          const key = u.agentId || u.id;
                          return (
                            <label
                              key={`af-ag-${key || i}`}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={allocAgents.includes(key)}
                                onChange={(e) =>
                                  setAllocAgents((prev) =>
                                    e.target.checked
                                      ? [...prev, key]
                                      : prev.filter((x) => x !== key)
                                  )
                                }
                              />
                              <span className="text-slate-800 truncate">{u.name}</span>
                            </label>
                          );
                        })}
                    </div>
                  </div>

                  {/* Unclassified accounts, said plainly rather than left to
                      be discovered by a filter that returns nothing. */}
                  {allocBanklessCount > 0 && (
                    <div className="md:col-span-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900">
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>
                        {allocBanklessCount.toLocaleString('en-IN')} of these accounts have no bank
                        recorded — they were uploaded before the bank field existed. They still
                        appear under whichever bank you tick. To give them a real bank, use{' '}
                        <strong>Settings → Fill in bank from branch</strong>.
                      </span>
                    </div>
                  )}

                  <div className="md:col-span-3 flex items-center justify-between gap-3">
                    <span className="text-slate-600 font-semibold">
                      {allocationCandidates.length.toLocaleString('en-IN')} account
                      {allocationCandidates.length === 1 ? '' : 's'} match
                      {allocBanks.length + allocZones.length + allocBranches.length + allocAgents.length === 0
                        ? ' (no filter set)'
                        : ''}
                      {selectedAccountIds.length > 0
                        ? ` · ${selectedAccountIds.length.toLocaleString('en-IN')} selected`
                        : ''}
                    </span>
                    {allocBanks.length + allocZones.length + allocBranches.length + allocAgents.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setAllocBanks([]);
                          setAllocZones([]);
                          setAllocBranches([]);
                          setAllocAgents([]);
                          setSelectedAccountIds([]);
                        }}
                        className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg font-bold text-slate-700 transition"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                </div>

                {/* Table for bulk selection */}
                <div className="overflow-x-auto border border-slate-200 rounded-xl">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200 uppercase">
                      <tr>
                        <th className="p-3 text-center">
                          {/* This tick covers THIS PAGE only.
                              Every account matching the filters is a separate,
                              named button below the table, so allocating four
                              hundred accounts is never something that happens
                              from a checkbox you thought applied to 25 rows. */}
                          <input
                            type="checkbox"
                            checked={allocPageAllSelected}
                            title="Select the accounts on this page"
                            onChange={() => {
                              if (allocPageAllSelected) {
                                setSelectedAccountIds((prev) =>
                                  prev.filter((id) => !allocPageIds.includes(id))
                                );
                              } else {
                                setSelectedAccountIds((prev) =>
                                  Array.from(new Set([...prev, ...allocPageIds]))
                                );
                              }
                            }}
                            className="rounded cursor-pointer"
                          />
                        </th>
                        <th className="p-3">Account ID</th>
                        <th className="p-3">Customer Name</th>
                        <th className="p-3">Branch &amp; Code</th>
                        {/* Just "Product" here: this table already has a
                            Branch & Code column of its own two places to the
                            left, and the cell under this one only ever held
                            the product. */}
                        <th className="p-3">Product</th>
                        <th className="p-3">Overdue Amount</th>
                        <th className="p-3">PTP Count</th>
                        <th className="p-3">Currently Assigned To</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-700">
                      {pagedAllocationCandidates.map((acc, idx) => (
                        <tr
                          key={acc.id || acc.accountId || `acc-bulk-row-${idx}`}
                          className={`hover:bg-slate-50 cursor-pointer transition ${
                            selectedAccountIds.includes(acc.accountId) ? 'bg-blue-50/60' : ''
                          }`}
                          onClick={() => handleToggleSelect(acc.accountId)}
                        >
                          <td className="p-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedAccountIds.includes(acc.accountId)}
                              onChange={() => handleToggleSelect(acc.accountId)}
                              className="rounded cursor-pointer"
                            />
                          </td>
                          <td className="p-3 font-mono font-bold text-blue-600">{acc.accountId}</td>
                          <td className="p-3 font-bold text-slate-900">{acc.customerName}</td>
                          <td className="p-3">
                            <span className="font-medium text-slate-800">{acc.branch}</span>
                            <span className="block text-[10px] font-mono text-slate-400">{acc.branchCode || 'BR-01'}</span>
                          </td>
                          <td className="p-3">{productOf(acc)}</td>
                          <td className="p-3 font-mono font-bold text-red-600">{formatINR(acc.overdueAmount)}</td>
                          <td className="p-3 font-semibold text-blue-700">{acc.ptpCount ?? 0} PTPs</td>
                          <td className="p-3 text-slate-700">
                            {/* An unallocated account said so, rather than
                                printing "undefined (undefined)". */}
                            {acc.assignedAgentName || acc.assignedAgentId ? (
                              <>
                                {acc.assignedAgentName || '—'}
                                {acc.assignedAgentId ? ` (${acc.assignedAgentId})` : ''}
                              </>
                            ) : (
                              <span className="text-amber-700 font-semibold">Not allocated</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {pagedAllocationCandidates.length === 0 && (
                        <tr>
                          <td colSpan={8} className="p-6 text-center text-slate-500">
                            No accounts match these filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pager, and the only place a whole-book selection can be
                    made -- named and counted. */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setAllocPage((p) => Math.max(1, p - 1))}
                      disabled={allocPage <= 1}
                      className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg font-bold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition"
                    >
                      Previous
                    </button>
                    <span className="text-slate-600 font-semibold">
                      Page {allocPaged.page} of {allocPaged.pageCount}
                      {allocPaged.total > 0
                        ? ` · showing ${allocPaged.from}–${allocPaged.to} of ${allocPaged.total.toLocaleString(
                            'en-IN'
                          )}`
                        : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => setAllocPage((p) => Math.min(allocTotalPages, p + 1))}
                      disabled={allocPage >= allocTotalPages}
                      className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg font-bold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 transition"
                    >
                      Next
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    {allocationCandidates.length > allocPageIds.length && (
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedAccountIds(allocationCandidates.map((a) => a.accountId))
                        }
                        className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold transition"
                      >
                        Select all {allocationCandidates.length.toLocaleString('en-IN')} matching
                      </button>
                    )}
                    {selectedAccountIds.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedAccountIds([])}
                        className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg font-bold text-slate-700 transition"
                      >
                        Clear selection
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* The Shortlist Customers menu is gone.

              Shortlisting by rating, amount from-to, name, agent and account
              number now happens in the filter bar above the Loan Accounts
              list, which is where the accounts already are. Keeping a second
              screen that loaded the whole book to do the same job was the
              expensive half of this.

              This block is removed rather than left unreachable, so nobody
              later restores a menu entry and gets a screen that reads every
              account again. */}

          {/* 4. GEO-TAGGED PHOTOS GALLERY */}
          {activeSection === 'photos' && (
            <div className="space-y-4">
              {/* Counts per agent first; the records only after one is
                  chosen. */}
              <LogScopeGate
                collectionName="photos"
                noun="geo-tagged photos"
                selectedAgent={logAgent}
                onSelectAgent={setLogAgent}
              />

              <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                      <Camera className="w-4 h-4 text-sky-600" />
                      <span>SRMS Geo-Tagged Watermarked Photographs</span>
                    </h3>
                    <p className="text-xs text-slate-500">
                      Photographs stamped with official SRMS box, GPS coordinates, Agent ID, Account ID, and recorded to Firestore
                    </p>
                  </div>
                  <span className="text-xs font-mono text-sky-700 bg-sky-50 px-3 py-1 rounded-lg border border-sky-200 font-semibold">
                    {photos.length} Photos in Cloud Firestore
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {gatedPhotos.map((ph, idx) => (
                    <div
                      key={ph.id || `photo-card-${idx}`}
                      className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm space-y-2 hover:shadow-md transition"
                    >
                      <div className="relative bg-slate-900 aspect-[4/3] flex items-center justify-center overflow-hidden">
                        <img
                          src={(ph.downloadUrl || ph.dataUrl)}
                        title="Click to open the full-size photo"
                        onClick={() => {
                          const u = ph.downloadUrl || ph.dataUrl;
                          if (u) window.open(u, "_blank", "noopener");
                        }}
                          alt="Watermarked Geo-Photo"
                          className="w-full h-full object-contain cursor-zoom-in hover:opacity-90 transition"
                        />
                      </div>
                      <div className="p-4 space-y-2.5 text-xs">
                        <div className="flex items-center justify-between font-bold">
                          <span className="text-blue-600 font-mono">{ph.accountId}</span>
                          <span className="text-slate-400 font-mono text-[10px]">{ph.driveFileId}</span>
                        </div>
                        <div className="p-2.5 bg-slate-50 rounded-lg font-mono text-[11px] text-slate-700 border border-slate-100 space-y-1">
                          <div className="flex justify-between text-slate-600">
                            <span>Date &amp; Time:</span>
                            <span className="font-medium text-slate-900">{ph.date} {ph.time}</span>
                          </div>
                          <div className="flex justify-between text-emerald-700">
                            <span>GPS Coordinates:</span>
                            <span className="font-semibold">
                              {typeof ph.latitude === 'number' ? ph.latitude.toFixed(6) : '—'}° N, {typeof ph.longitude === 'number' ? ph.longitude.toFixed(6) : '—'}° E
                            </span>
                          </div>
                          <div className="flex justify-between text-slate-500">
                            <span>Agent:</span>
                            <span>{ph.agentId} ({ph.agentName})</span>
                          </div>
                        </div>
                        <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 space-y-1">
                          <p
                            className={`text-xs italic ${
                              ph.agentRemark ? 'text-slate-600' : 'text-slate-400'
                            }`}
                          >
                            {ph.agentRemark
                              ? `"${ph.agentRemark}"`
                              : 'No note was added for this photo.'}
                          </p>
                          {ph.noteEdited && (
                            <p className="text-[10px] text-amber-700 font-semibold">
                              Note filled in later by {ph.noteEditedBy || 'a user'}. The
                              picture itself shows what was written at capture.
                            </p>
                          )}
                        </div>

                        <div className="flex gap-2 mt-1">
                          <button
                            type="button"
                            onClick={() => handleEditPhotoNote(ph)}
                            disabled={editingNoteId === (ph.id || ph.photoId)}
                            className="flex-1 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 font-bold text-[11px] hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                          >
                            {editingNoteId === (ph.id || ph.photoId)
                              ? 'Saving…'
                              : ph.agentRemark
                              ? 'Edit Note'
                              : 'Add Note'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeletePhoto(ph)}
                            disabled={deletingPhotoId === (ph.id || ph.photoId)}
                            className="flex-1 px-3 py-2 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 font-bold text-[11px] hover:bg-rose-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                          >
                            {deletingPhotoId === (ph.id || ph.photoId)
                              ? 'Deleting…'
                              : 'Delete Photo'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 6. CLOUD FIRESTORE SERVICE OPTIMIZATION */}
          {activeSection === 'firestore' && (
            <div className="space-y-5 text-xs">
              {/* Header card with health & latency */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 bg-blue-50 text-blue-600 rounded-xl border border-blue-200">
                        <Database className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-black text-base text-slate-900 flex items-center gap-2">
                          <span>Cloud Firestore Real-Time Optimization Engine</span>
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Direct, sub-50ms persistent document store with real-time onSnapshot synchronization &amp; local cache resilience.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleTestFirestoreConnection}
                      disabled={isTestingFirestore}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold flex items-center gap-2 shadow-sm transition cursor-pointer disabled:opacity-50 text-xs"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${isTestingFirestore ? 'animate-spin' : ''}`} />
                      <span>{isTestingFirestore ? 'Testing Latency...' : 'Run Latency Benchmark'}</span>
                    </button>
                    <button
                      onClick={() => exportAllBackendData()}
                      className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl border border-slate-200 font-semibold flex items-center gap-1.5 transition cursor-pointer text-xs"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>Export Collections</span>
                    </button>
                  </div>
                </div>

                {firestoreTestMsg && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl flex items-center justify-between font-mono text-xs">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>{firestoreTestMsg}</span>
                    </div>
                    <button onClick={() => setFirestoreTestMsg(null)} className="text-emerald-700 hover:text-emerald-900 cursor-pointer">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* 4 Performance Metric Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                    <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Average Latency</div>
                    <div className="text-xl font-black text-slate-900 font-mono">
                      {firestoreStats.averageLatencyMs.toFixed(0)} <span className="text-xs font-normal text-slate-500">ms</span>
                    </div>
                    <div className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> Sub-50ms Fast Route
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                    <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Real-Time Writes</div>
                    <div className="text-xl font-black text-blue-600 font-mono">
                      {firestoreStats.writeCount}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      Synchronized documents
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                    <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Live Reads</div>
                    <div className="text-xl font-black text-indigo-600 font-mono">
                      {firestoreStats.readCount}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      Query snapshots served
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                    <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Engine Status</div>
                    <div className="text-base font-black text-emerald-700 flex items-center gap-1.5 mt-1">
                      <Shield className="w-4 h-4 text-emerald-600" />
                      <span>OPTIMIZED</span>
                    </div>
                    <div className="text-[10px] text-slate-500">
                      Offline-capable &amp; Secured
                    </div>
                  </div>
                </div>

                {/* Firestore Collections Selector Tabs */}
                <div className="flex items-center gap-2 overflow-x-auto pb-1 text-[11px]">
                  {[
                    { id: 'Accounts', label: 'Accounts', count: accounts.length },
                    { id: 'Recoveries', label: 'Recoveries', count: scopedRecoveries.length },
                    { id: 'PTP', label: 'PTP Commitments', count: ptps.length },
                    { id: 'Followups', label: 'Follow-ups', count: followups.length },
                    { id: 'Visits', label: 'Field Visits', count: visits.length },
                    { id: 'Photos', label: 'Photos', count: photos.length },
                    { id: 'VoiceNotes', label: 'Voice Notes', count: voiceNotes.length },
                    { id: 'Documents', label: 'Documents', count: documents.length },
                  ].map((tab) => (
                    <button
                      key={`fs-col-tab-${tab.id}`}
                      onClick={() => setActiveCollectionTab(tab.id)}
                      className={`px-3 py-1.5 rounded-xl font-semibold whitespace-nowrap transition cursor-pointer flex items-center gap-1.5 ${
                        activeCollectionTab === tab.id
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900 border border-slate-200'
                      }`}
                    >
                      <span>{tab.label}</span>
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono ${
                        activeCollectionTab === tab.id ? 'bg-blue-800 text-white' : 'bg-slate-200 text-slate-700'
                      }`}>
                        {tab.count}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Live Firestore Document Inspector */}
                <div className="bg-slate-950 text-slate-200 p-4 rounded-xl font-mono text-[11px] overflow-x-auto max-h-96 shadow-inner border border-slate-800">
                  {activeCollectionTab === 'Accounts' && (
                    <table className="w-full text-left">
                      <thead className="text-slate-400 border-b border-slate-800 pb-1">
                        <tr>
                          <th className="p-1.5">Document ID</th>
                          <th className="p-1.5">Customer Name</th>
                          <th className="p-1.5">Product</th>
                          <th className="p-1.5">Overdue</th>
                          <th className="p-1.5">Agent</th>
                          <th className="p-1.5">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800 text-slate-300">
                        {accounts.slice(0, collShown).map((a, idx) => (
                          <tr key={a.id || a.accountId || `fs-acc-${idx}`}>
                            <td className="p-1.5 text-blue-400 font-bold">{a.accountId}</td>
                            <td className="p-1.5">{a.customerName}</td>
                            <td className="p-1.5">{productOf(a)}</td>
                            <td className="p-1.5 text-rose-400 font-mono">₹{a.overdueAmount?.toLocaleString('en-IN')}</td>
                            <td className="p-1.5 text-amber-300">{a.assignedAgentName || a.assignedAgentId}</td>
                            <td className="p-1.5"><span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-800">{a.accountStatus}</span></td>
                          </tr>
                        ))}
                        {accounts.length > collShown && (
                          <tr>
                            <td colSpan={6} className="p-2 text-center">
                              <button type="button" onClick={() => setCollShown((n) => n + 15)} className="px-4 py-1.5 rounded-lg bg-slate-800 text-slate-200 text-xs font-bold hover:bg-slate-700">
                                Show more ({accounts.length - collShown} left)
                              </button>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}

                  {activeCollectionTab === 'Recoveries' && (
                    <table className="w-full text-left">
                      <thead className="text-slate-400 border-b border-slate-800 pb-1">
                        <tr>
                          <th className="p-1.5">Receipt #</th>
                          <th className="p-1.5">Account ID</th>
                          <th className="p-1.5">Amount</th>
                          <th className="p-1.5">Date</th>
                          <th className="p-1.5">Payment Mode</th>
                          <th className="p-1.5">Commission</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800 text-slate-300">
                        {scopedRecoveries.slice(0, collShown).map((r, idx) => (
                          <tr key={r.id || `fs-rec-${idx}`}>
                            <td className="p-1.5 text-emerald-400 font-bold">{r.receiptNumber}</td>
                            <td className="p-1.5 text-blue-400">{r.accountId}</td>
                            <td className="p-1.5 text-white font-mono">₹{r.amount?.toLocaleString('en-IN')}</td>
                            <td className="p-1.5 text-slate-400">{r.recoveryDate}</td>
                            <td className="p-1.5">{r.paymentMode}</td>
                            <td className="p-1.5 text-emerald-400 font-mono">₹{r.commissionAmount?.toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                        {scopedRecoveries.length > collShown && (
                          <tr>
                            <td colSpan={6} className="p-2 text-center">
                              <button type="button" onClick={() => setCollShown((n) => n + 15)} className="px-4 py-1.5 rounded-lg bg-slate-800 text-slate-200 text-xs font-bold hover:bg-slate-700">
                                Show more ({scopedRecoveries.length - collShown} left)
                              </button>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}

                  {activeCollectionTab === 'PTP' && (
                    <table className="w-full text-left">
                      <thead className="text-slate-400 border-b border-slate-800 pb-1">
                        <tr>
                          <th className="p-1.5">PTP ID</th>
                          <th className="p-1.5">Account ID</th>
                          <th className="p-1.5">Promised Date</th>
                          <th className="p-1.5">Amount</th>
                          <th className="p-1.5">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800 text-slate-300">
                        {ptps.slice(0, collShown).map((p, idx) => (
                          <tr key={p.id || `fs-ptp-${idx}`}>
                            <td className="p-1.5 text-amber-400 font-bold">{p.id}</td>
                            <td className="p-1.5 text-blue-400">{p.accountId}</td>
                            <td className="p-1.5 text-slate-300">{p.ptpDate}</td>
                            <td className="p-1.5 text-white font-mono">₹{p.amount?.toLocaleString('en-IN')}</td>
                            <td className="p-1.5">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] ${p.status === 'Achieved' ? 'bg-emerald-950 text-emerald-400' : p.status === 'Broken' ? 'bg-red-950 text-red-400' : 'bg-amber-950 text-amber-400'}`}>
                                {p.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {ptps.length > collShown && (
                          <tr>
                            <td colSpan={5} className="p-2 text-center">
                              <button type="button" onClick={() => setCollShown((n) => n + 15)} className="px-4 py-1.5 rounded-lg bg-slate-800 text-slate-200 text-xs font-bold hover:bg-slate-700">
                                Show more ({ptps.length - collShown} left)
                              </button>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}

                  {activeCollectionTab !== 'Accounts' &&
                    activeCollectionTab !== 'Recoveries' &&
                    activeCollectionTab !== 'PTP' && (
                    <p className="text-slate-300 p-2">
                      Viewing live Firestore documents for collection: <span className="text-blue-400 font-bold">{activeCollectionTab}</span>. Synchronized in real-time with sub-50ms latency.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 7. USERS & ROLES (RBAC) */}
          {activeSection === 'users' && (
            <div className="space-y-4">
              {userAdminFeedback && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-semibold flex items-center justify-between shadow-xs">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>{userAdminFeedback}</span>
                  </div>
                  <button onClick={() => setUserAdminFeedback(null)} className="text-emerald-700 hover:text-emerald-900">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                      <Users className="w-4 h-4 text-purple-600" />
                      <span>User Management &amp; Role-Based Access Control</span>
                    </h3>
                    <p className="text-xs text-slate-500">
                      Configure agent user IDs, secure passwords, branch assignments, zone credentials, and monthly recovery targets
                    </p>
                  </div>
                  <button
                    onClick={() => setIsCreateUserOpen(true)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 shadow-sm transition cursor-pointer"
                  >
                    <Plus className="w-4 h-4" />
                    <span>+ Create New User</span>
                  </button>
                </div>

                <div className="overflow-x-auto border border-slate-200 rounded-xl">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200 uppercase">
                      <tr>
                        <th className="p-3.5">User</th>
                        <th className="p-3.5">Role</th>
                        <th className="p-3.5">User / Agent ID</th>
                        <th className="p-3.5">Assigned Bank</th>
                        <th className="p-3.5">Branch &amp; Zone</th>
                        <th className="p-3.5">Mobile</th>
                        <th className="p-3.5">Monthly Target</th>
                        <th className="p-3.5">Status</th>
                        <th className="p-3.5 text-right">Admin Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-700">
                      {users.map((u, idx) => (
                        <tr key={u.id || u.agentId || `user-row-${idx}`} className="hover:bg-slate-50 transition">
                          <td className="p-3.5">
                            <div className="font-bold text-slate-900">{u.name}</div>
                            {/* Show the Login ID, not the hidden internal auth
                                address. This is what the person actually types. */}
                            <div className="text-slate-500 text-[11px]">
                              Login ID:{' '}
                              <span className="font-mono font-semibold text-slate-600">
                                {u.loginId || u.username || u.agentId || '—'}
                              </span>
                            </div>
                            {u.contactEmail ? (
                              <div className="text-slate-400 text-[10px]">{u.contactEmail}</div>
                            ) : null}
                          </td>
                          <td className="p-3.5">
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase border ${
                                u.role === 'admin'
                                  ? 'bg-purple-50 text-purple-700 border-purple-200'
                                  : u.role === 'agent'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : 'bg-blue-50 text-blue-700 border-blue-200'
                              }`}
                            >
                              {u.role}
                            </span>
                          </td>
                          <td className="p-3.5 font-mono font-bold text-blue-600">
                            <div>{u.agentId || u.id}</div>
                            <span className="text-[10px] text-slate-400 font-normal">PIN/Pass Configured</span>
                          </td>
                          <td className="p-3.5">
                            {u.bank ? (
                              <span className="px-2 py-0.5 bg-blue-50 text-blue-800 rounded font-semibold text-[11px] border border-blue-200">
                                {u.bank}
                              </span>
                            ) : (
                              <span className="text-slate-400 text-[11px] italic">All Banks</span>
                            )}
                          </td>
                          <td className="p-3.5 text-slate-700">
                            <div className="font-medium text-slate-900">{u.branch || '—'}</div>
                            <div className="text-[10px] text-slate-500">{u.zone || '—'}</div>
                          </td>
                          <td className="p-3.5 font-mono text-slate-700">{u.mobile}</td>
                          <td className="p-3.5 font-mono text-amber-700 font-bold">
                            {u.monthlyTarget ? formatINR(u.monthlyTarget) : '—'}
                          </td>
                          <td className="p-3.5">
                            <span
                              className={`text-[10px] font-bold px-2.5 py-1 rounded-md ${
                                u.active
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-slate-100 text-slate-500'
                              }`}
                            >
                              {u.active ? 'Active' : 'Inactive'}
                            </span>
                            {/* Plan, for agents only. Shown, never enforced --
                                an expired date is for you to act on, not for
                                the app to lock a field agent out over. */}
                            {u.role === 'agent' &&
                              (() => {
                                const ps = planStatusOf(u);
                                if (ps.status === 'free') return null;
                                const tone =
                                  ps.status === 'expired'
                                    ? 'bg-rose-50 text-rose-700 border-rose-200'
                                    : ps.status === 'expiring'
                                    ? 'bg-amber-50 text-amber-800 border-amber-200'
                                    : ps.status === 'active'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-slate-50 text-slate-500 border-slate-200';
                                return (
                                  <div
                                    className={`mt-1 inline-block text-[9px] font-bold px-2 py-0.5 rounded-full border ${tone}`}
                                    title={ps.endDate ? `Ends ${ps.endDate}` : undefined}
                                  >
                                    {ps.label}
                                  </div>
                                );
                              })()}
                          </td>
                          <td className="p-3.5 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => openEditUserModal(u)}
                                className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-[11px] font-semibold border border-slate-200 transition cursor-pointer flex items-center gap-1"
                                title="Edit user profile & details"
                              >
                                <Edit className="w-3 h-3" />
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => openChangePasswordModal(u)}
                                className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-lg text-[11px] font-semibold border border-amber-200 transition cursor-pointer flex items-center gap-1"
                                title="Change or reset user password & PIN"
                              >
                                <FolderLock className="w-3 h-3" />
                                <span>Password</span>
                              </button>
                              <button
                                onClick={() => toggleUserActive(u.id)}
                                className={`px-2 py-1 rounded-lg text-[11px] font-semibold border transition cursor-pointer ${
                                  u.active
                                    ? 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200'
                                    : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200'
                                }`}
                              >
                                {u.active ? 'Deactivate' : 'Activate'}
                              </button>
                              {currentUser.role === 'admin' && u.id !== currentUser.id && (
                                <button
                                  onClick={() => openDeleteUserModal(u)}
                                  className="px-2 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-lg text-[11px] font-semibold border border-rose-200 transition cursor-pointer flex items-center gap-1"
                                  title="Delete User (Reassigns accounts to Management Queue & preserves all notes/recoveries)"
                                >
                                  <Trash2 className="w-3 h-3 text-rose-600" />
                                  <span>Delete</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* 8. FOLLOW-UPS */}
          {activeSection === 'followups' && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-sm">
              {/* Counts per agent first; the records only after one is
                  chosen. */}
              <LogScopeGate
                collectionName="followups"
                noun="follow-up calls"
                selectedAgent={logAgent}
                onSelectAgent={setLogAgent}
              />

              <h3 className="font-bold text-sm text-slate-900">All Follow-up Calls &amp; Customer Interactions</h3>
              <div className="space-y-3 text-xs">
                {gatedFollowups.map((f, idx) => (
                  <div key={f.id || `fup-card-${idx}`} className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-slate-900">{f.customerName} ({f.accountId})</span>
                      <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 font-semibold">{f.status}</span>
                    </div>
                    <p className="text-slate-700">{f.discussionDetails}</p>
                    <div className="flex justify-between text-[11px] text-slate-500 font-mono pt-1">
                      <span>Logged by: {f.agentName} ({f.agentId})</span>
                      <span>Next: {f.nextFollowUpDate} at {f.nextFollowUpTime}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 9. PTP COMMITMENTS */}
          {activeSection === 'ptp' && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5 shadow-sm">
              {/* Counts per agent first; the records only after one is
                  chosen. */}
              <LogScopeGate
                collectionName="ptps"
                noun="PTP commitments"
                selectedAgent={logAgent}
                onSelectAgent={setLogAgent}
              />

              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-purple-600" />
                      <span>Promise to Pay (PTP) Master Directory</span>
                    </h3>
                    <span className="text-[10px] font-mono font-bold px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full border border-blue-200">
                      Cloud Firestore Real-Time
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Track all borrower repayment commitments, evaluate broken schedules, and execute batch synchronization
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsPTPDigestOpen(true)}
                    className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 shadow-xs transition cursor-pointer ${
                      ptpPreview.expiredCount > 0
                        ? 'bg-gradient-to-r from-amber-600 to-red-600 hover:from-amber-700 hover:to-red-700 text-white shadow-amber-600/20'
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-600/20'
                    }`}
                  >
                    <Zap className="w-4 h-4 text-amber-300 fill-amber-300" />
                    <span>Run PTP Digest</span>
                    {ptpPreview.expiredCount > 0 && (
                      <span className="px-1.5 py-0.5 bg-white text-red-700 text-[10px] font-black rounded-full shadow-xs">
                        {ptpPreview.expiredCount} Expired
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Status Filter Pills */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {[
                  { id: 'ALL', label: 'All PTPs', count: ptps.length },
                  {
                    id: 'Pending',
                    label: 'Pending',
                    count: ptps.filter((p) => p.status === 'Pending').length,
                  },
                  {
                    id: 'Expired',
                    label: 'Expired Overdue',
                    count: ptpPreview.expiredCount,
                    highlight: ptpPreview.expiredCount > 0,
                  },
                  {
                    id: 'DueToday',
                    // Was hard-coded to "(23-Aug)" -- a date from whenever the
                    // screen was written, printed on a tab whose count is
                    // genuinely today's. It now shows today.
                    label: `Due Today (${new Date().toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                    })})`,
                    count: ptpPreview.dueTodayCount,
                  },
                  {
                    id: 'Broken',
                    label: 'Broken',
                    count: ptps.filter((p) => p.status === 'Broken').length,
                  },
                  {
                    id: 'Achieved',
                    label: 'Achieved',
                    count: ptps.filter((p) => p.status === 'Achieved').length,
                  },
                ].map((tab) => (
                  <button
                    key={`ptp-tab-${tab.id}`}
                    onClick={() => setPtpFilterStatus(tab.id)}
                    className={`px-3 py-1.5 rounded-lg font-medium transition cursor-pointer flex items-center gap-1.5 ${
                      ptpFilterStatus === tab.id
                        ? 'bg-slate-900 text-white shadow-xs'
                        : tab.highlight
                        ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <span>{tab.label}</span>
                    <span
                      className={`text-[10px] font-mono px-1.5 py-0.2 rounded-full ${
                        ptpFilterStatus === tab.id
                          ? 'bg-white/20 text-white'
                          : tab.highlight
                          ? 'bg-red-200 text-red-800 font-bold'
                          : 'bg-slate-200 text-slate-700'
                      }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* PTP Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
                {ptps
                  // Nothing until an agent is chosen above.
                  .filter(belongsToLogAgent)
                  .filter((p) => {
                    if (ptpFilterStatus === 'ALL') return true;
                    if (ptpFilterStatus === 'Expired') return p.status === 'Pending' && p.ptpDate < todayIso();
                    if (ptpFilterStatus === 'DueToday') return p.status === 'Pending' && p.ptpDate === todayIso();
                    return p.status === ptpFilterStatus;
                  })
                  .map((p, idx) => {
                    const isExpired = p.status === 'Pending' && p.ptpDate < todayIso();
                    const isDueToday = p.status === 'Pending' && p.ptpDate === todayIso();

                    return (
                      <div
                        key={p.id || `ptp-card-${idx}`}
                        className={`p-4 rounded-xl border space-y-3 transition shadow-xs hover:shadow-md ${
                          isExpired
                            ? 'bg-red-50/60 border-red-200 ring-1 ring-red-200'
                            : p.status === 'Broken'
                            ? 'bg-slate-50 border-slate-200 opacity-90'
                            : p.status === 'Achieved'
                            ? 'bg-emerald-50/50 border-emerald-200'
                            : 'bg-white border-slate-200'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-bold text-slate-900 text-sm">{p.customerName}</div>
                            <span className="text-blue-600 font-mono text-[11px] font-bold">{p.accountId}</span>
                          </div>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                              p.status === 'Achieved'
                                ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                : p.status === 'Broken'
                                ? 'bg-red-100 text-red-800 border-red-300'
                                : isExpired
                                ? 'bg-rose-100 text-rose-800 border-rose-300 animate-pulse'
                                : isDueToday
                                ? 'bg-amber-100 text-amber-800 border-amber-300'
                                : 'bg-blue-100 text-blue-800 border-blue-300'
                            }`}
                          >
                            {isExpired ? 'Expired Overdue' : isDueToday ? 'Due Today' : p.status}
                          </span>
                        </div>

                        <div className="flex justify-between items-center font-mono bg-slate-50/90 p-2.5 rounded-lg border border-slate-200 text-xs">
                          <span className="text-purple-700 font-bold text-sm">{formatINR(p.amount)}</span>
                          <div className="text-right">
                            <span className="font-bold text-slate-800 block">{p.ptpDate}</span>
                            <span className="text-[10px] text-slate-500 font-sans">{p.ptpMode}</span>
                          </div>
                        </div>

                        <p className="text-slate-600 text-xs italic bg-white p-2 rounded-md border border-slate-100 line-clamp-2">
                          "{p.customerCommitment}"
                        </p>

                        <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2 text-[11px] text-slate-500">
                          <span className="truncate">Agent: {p.agentName}</span>
                          <div className="flex items-center gap-3 shrink-0">
                            {/*
                              Delete a promise.

                              Offered only to somebody the DATABASE will
                              actually allow -- an administrator, or the agent
                              who recorded it. Showing it to anybody else
                              would repeat the trap the account delete had: a
                              button that is offered, then refused, and
                              reported as a broken permission.
                            */}
                            {(currentUser.role === 'admin' ||
                              (currentUser.role as string) === 'super_admin' ||
                              p.agentId === currentUser.agentId ||
                              p.agentId === currentUser.id) && (
                              <button
                                onClick={async () => {
                                  const ok = window.confirm(
                                    `Delete this promise?\n\n${p.customerName} — ${formatINR(p.amount)} due ${p.ptpDate}\n\nThe account's promise status will be rebuilt from whatever promises remain. This cannot be undone.`
                                  );
                                  if (!ok) return;
                                  const reason = window.prompt('Reason (optional):') || '';
                                  const res = await deletePTP(p.id, reason);
                                  if (!res.success) alert(res.message);
                                }}
                                className="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-lg font-bold cursor-pointer inline-flex items-center gap-1"
                                title="Delete this promise to pay"
                              >
                                <Trash2 className="w-3 h-3" />
                                <span>Delete</span>
                              </button>
                            )}
                            <button
                              onClick={() => {
                                const acc = accounts.find((a) => a.accountId === p.accountId);
                                if (acc) {
                                  setSelectedAccount(acc);
                                  setIsSingleCustomerModalOpen(true);
                                }
                              }}
                              className="text-blue-600 hover:text-blue-800 font-semibold cursor-pointer"
                            >
                              View Account →
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* HIERARCHY & BRANCHES SECTION */}
          {activeSection === 'hierarchy' && <HierarchyManagementSection />}

          {/* COMMISSION MANAGEMENT SECTION */}
          {activeSection === 'commission' && <CommissionManagementSection />}

          {/* COMMISSION RULE OPTION (dynamic rules, admin only; not yet connected to payouts) */}
          {activeSection === 'commissionRuleOption' && <DynamicCommissionSection />}

          {/* 10. RECOVERY RECEIPTS */}
          {activeSection === 'recovery' && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-sm">
              {/* Counts per agent first; the records only after one is
                  chosen. */}
              <LogScopeGate
                collectionName="recoveries"
                noun="collections"
                selectedAgent={logAgent}
                onSelectAgent={setLogAgent}
              />

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-sm text-slate-900">All Collections &amp; Recovery Receipts</h3>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                      {recoveryMonthFilter === currentMonthStr ? `Current Month (${currentMonthStr})` : recoveryMonthFilter === 'ALL' ? 'All Months' : recoveryMonthFilter}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">Immutable recovery transactions with hierarchical commission ledger verification</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl">
                    <span className="text-[11px] font-bold text-slate-600 pl-2">Filter Month:</span>
                    <select
                      value={recoveryMonthFilter}
                      onChange={(e) => setRecoveryMonthFilter(e.target.value)}
                      className="bg-white border border-slate-300 rounded-lg px-2.5 py-1 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                    >
                      <option value={currentMonthStr}>Current Month ({currentMonthStr})</option>
                      <option value="ALL">All Recorded Months</option>
                      {availableDashMonths
                        .filter((m) => m !== currentMonthStr)
                        .map((m) => (
                          <option key={`rec-m-${m}`} value={m}>
                            {m}
                          </option>
                        ))}
                    </select>
                  </div>
                  <button
                    onClick={() => setActiveSection('commission')}
                    className="px-3.5 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
                  >
                    <Percent className="w-3.5 h-3.5" />
                    <span>Open Commission Rules &amp; Simulator</span>
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto border border-slate-200 rounded-xl">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200 uppercase">
                    <tr>
                      <th className="p-3">Receipt No</th>
                      <th className="p-3">Account &amp; Customer</th>
                      <th className="p-3">Amount</th>
                      <th className="p-3">Calculated Commission</th>
                      <th className="p-3">Rule / Priority</th>
                      <th className="p-3">Payment Mode</th>
                      <th className="p-3">Agent</th>
                      <th className="p-3">Date</th>
                      <th className="p-3 text-right">Status / Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {scopedRecoveries
                      // Nothing until an agent is chosen above.
                      .filter(belongsToLogAgent)
                      .filter((r) => {
                        if (recoveryMonthFilter !== 'ALL' && (!r.recoveryDate || !r.recoveryDate.startsWith(recoveryMonthFilter))) {
                          return false;
                        }
                        return true;
                      })
                      .map((r, idx) => (
                      <tr key={r.id || r.receiptNumber || `rec-row-${idx}`} className={`hover:bg-slate-50 transition ${r.isReversed ? 'bg-slate-50/70 opacity-80' : ''}`}>
                        <td className="p-3 font-mono text-blue-600 font-bold">{r.receiptNumber}</td>
                        <td className="p-3 font-bold text-slate-900">
                          {r.customerName} ({r.accountId})
                          {r.isReversed && (
                            <span className="ml-2 text-[10px] text-rose-600 font-bold bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200">
                              REVERSED
                            </span>
                          )}
                        </td>
                        <td className={`p-3 font-mono font-bold ${r.isReversed ? 'line-through text-slate-400' : 'text-emerald-600'}`}>
                          {formatINR(r.amount)}
                        </td>
                        <td className="p-3 font-mono font-bold text-amber-600">
                          {formatINR(r.commissionAmount)}
                          <span className="ml-1 text-[10px] text-slate-400 font-normal">({r.commissionRate || 10}%)</span>
                        </td>
                        <td className="p-3">
                          {r.appliedPriorityLevel ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
                              Priority {r.appliedPriorityLevel}
                            </span>
                          ) : (
                            <span className="text-[10px] text-slate-400">Default Policy</span>
                          )}
                        </td>
                        <td className="p-3">{r.paymentMode}</td>
                        <td className="p-3">{r.agentName} ({r.agentId})</td>
                        <td className="p-3 text-slate-500">{r.recoveryDate}</td>
                        <td className="p-3 text-right">
                          {r.isReversed ? (
                            <div className="text-right">
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-200 inline-block">
                                Reversed
                              </span>
                              {r.reversalReason && (
                                <span className="text-[10px] text-slate-500 block truncate max-w-[150px] ml-auto mt-0.5" title={r.reversalReason}>
                                  {r.reversalReason}
                                </span>
                              )}
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setReversalTargetRecovery(r);
                                setReversalReasonInput('');
                                setIsReversalModalOpen(true);
                              }}
                              className="px-2.5 py-1 text-xs font-semibold text-rose-600 hover:text-rose-800 hover:bg-rose-50 rounded-lg border border-rose-200 flex items-center gap-1 cursor-pointer transition ml-auto"
                              title="Reverse this recovery entry and restore account balance"
                            >
                              <RotateCcw className="w-3 h-3" />
                              <span>Reverse</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 11. FIELD VISITS */}
          {activeSection === 'visits' && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-sm">
              {/* Counts per agent first; the records only after one is
                  chosen. */}
              <LogScopeGate
                collectionName="visits"
                noun="field visits"
                selectedAgent={logAgent}
                onSelectAgent={setLogAgent}
              />

              <h3 className="font-bold text-sm text-slate-900">Field Visits &amp; GPS Location Log</h3>
              <div className="space-y-3 text-xs">
                {gatedVisits.map((v, idx) => (
                  <div key={v.id || `visit-card-${idx}`} className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-bold text-slate-900">{v.customerName} ({v.accountId})</span>
                        <p className="text-slate-500 text-[11px]">{v.address}</p>
                      </div>
                      <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-200">
                        {v.visitStatus}
                      </span>
                    </div>
                    <div className="flex justify-between font-mono text-emerald-700 bg-white p-2.5 rounded-lg border border-slate-200 text-[11px]">
                      <span>GPS: {typeof v.latitude === 'number' ? v.latitude.toFixed(6) : '—'}° N, {typeof v.longitude === 'number' ? v.longitude.toFixed(6) : '—'}° E</span>
                      <span className="text-slate-600">{v.date} ({v.startTime} - {v.endTime || 'Done'})</span>
                    </div>
                    <p className="text-slate-600">{v.visitRemarks}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 12. VOICE NOTES */}
          {activeSection === 'voice' && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-sm">
              {/* Counts per agent first; the records only after one is
                  chosen. */}
              <LogScopeGate
                collectionName="voicenotes"
                noun="voice notes"
                selectedAgent={logAgent}
                onSelectAgent={setLogAgent}
              />

              <h3 className="font-bold text-sm text-slate-900">Voice Notes Audio Library</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                {gatedVoiceNotes.map((vn, idx) => (
                  <div key={vn.id || `vn-card-${idx}`} className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Mic className="w-4 h-4 text-pink-600" />
                        <span className="font-bold text-slate-900">{vn.title}</span>
                      </div>
                      <span className="font-mono text-[10px] text-blue-600">{vn.driveFileId || vn.id}</span>
                    </div>
                    <p className="text-slate-500 text-xs">Account: {vn.accountId} • Agent: {vn.agentName}</p>
                    <p className="text-slate-700 bg-white p-3 rounded-lg border border-slate-200 italic">
                      "{vn.transcription}"
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 14. EXECUTIVE REPORTS */}
          {activeSection === 'reports' && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 text-xs shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
                <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-emerald-600" />
                  <span>Executive Reports &amp; MIS Analytics</span>
                </h3>
                <button
                  onClick={() => exportAllBackendData()}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-xs flex items-center gap-2 shadow-sm transition cursor-pointer border border-slate-700"
                  title="Download all system database sheets into a multi-tab Excel file"
                >
                  <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
                  <span>Download Full Backend Data (.xlsx)</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                  <h4 className="font-bold text-slate-900">Complete Database Archive</h4>
                  <p className="text-slate-500">12 multi-sheet workbooks containing all live records, accounts, recoveries &amp; agent notes.</p>
                  <button
                    onClick={() => exportAllBackendData()}
                    className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-xs transition flex items-center gap-1.5 cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Export Excel (.xlsx)</span>
                  </button>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                  <h4 className="font-bold text-slate-900">Daily Recovery Report</h4>
                  <p className="text-slate-500">Total ₹{totalRecoverySum.toLocaleString('en-IN')} recovered across all field agents.</p>
                  <button
                    onClick={() => exportAllBackendData()}
                    className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-xs transition flex items-center gap-1.5 cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    {/* This downloads the WHOLE backend export, not the daily
                        recovery report the heading above promises. Renamed
                        rather than left quietly misleading. */}
                    <span>Download Full Data Export</span>
                  </button>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                  <h4 className="font-bold text-slate-900">Agent Commission Ledger</h4>
                  <p className="text-slate-500">₹{totalCommissionSum.toLocaleString('en-IN')} commissions calculated for {commissionSettings.defaultRate}% base rate.</p>
                  <button
                    onClick={() => setActiveSection('commission')}
                    className="px-3.5 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold text-xs transition flex items-center gap-1.5 cursor-pointer"
                  >
                    <span>View Ledger</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 15. SYSTEM SETTINGS */}
          {activeSection === 'settings' && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 text-xs shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
                <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-blue-600" />
                  <span>System Configuration &amp; Storage Architecture</span>
                </h3>
                <button
                  onClick={() => exportAllBackendData()}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-xs flex items-center gap-2 shadow-sm transition cursor-pointer border border-slate-700"
                  title="Export full Excel backup file"
                >
                  <Download className="w-4 h-4 text-emerald-400" />
                  <span>Download Full Data Backup (.xlsx)</span>
                </button>
              </div>

              <div className="space-y-3">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex justify-between items-center">
                  <div>
                    <span className="font-bold text-slate-900 block">Full Backend Data Export Permission</span>
                    <span className="text-slate-500 text-[11px]">Allows Admin to download all accounts, users, logs, follow-ups, PTPs, visits, and commission history in Excel</span>
                  </div>
                  <button
                    onClick={() => exportAllBackendData()}
                    className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs flex items-center gap-1.5 transition cursor-pointer shadow-xs"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5" />
                    <span>Export All Data Now</span>
                  </button>
                </div>
                <div className="p-4 bg-rose-50/70 rounded-xl border border-rose-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                  <div>
                    <span className="font-bold text-rose-950 text-sm block">Bulk Account Deletion &amp; Storage Purge</span>
                    <span className="text-rose-700 text-[11px] block mt-0.5">
                      Clear customer accounts in bulk with granular controls: by specific agent, specific branch, manual multi-selection, or wipe all {accounts.length} active account records. Option to archive history or permanently hard-delete. Users, credentials, and branches remain safe.
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => openBulkDeleteModal('agent')}
                      className="px-3.5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 transition cursor-pointer shadow-xs"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Bulk Delete by Agent / Branch / All</span>
                    </button>
                  </div>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex justify-between items-center">
                  <div>
                    <span className="font-bold text-slate-900 block">Cloud Firestore</span>
                    {/* This said "Real-time sub-50ms" and "✓ Optimized &
                        Connected" as fixed text -- it would have said exactly
                        the same with the database unreachable. It now reports
                        the live connection and the measured response time. */}
                    <span className="text-slate-500 text-[11px]">
                      {firestoreSpeedStats?.averageLatencyMs
                        ? `Average response ${firestoreSpeedStats.averageLatencyMs}ms over the last operations`
                        : 'Real-time document storage and snapshot synchronisation'}
                    </span>
                  </div>
                  <span
                    className={`font-bold px-2.5 py-1 rounded-md border ${
                      firestoreSpeedStats?.connected
                        ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                        : 'text-rose-700 bg-rose-50 border-rose-200'
                    }`}
                  >
                    {firestoreSpeedStats?.connected ? '✓ Connected' : '• Not connected'}
                  </span>
                </div>
                {/* Commission rates and duplicate branches -- both admin
                    only, both hidden entirely from everyone else. */}
                <CommissionRatesSettings />
                <BranchMergeSettings />

                {/* How often each agent signs in. Counted from the activity
                    log already in hand -- no new reads, no new writes. */}
                <AgentLoginCounts />

                {/* Enquiries sent from the Join Us form on the public website. */}
                <EnquiriesPanel />

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex justify-between items-center">
                  <div>
                    <span className="font-bold text-slate-900 block">Future Database Migration Readiness</span>
                    <span className="text-slate-500 text-[11px]">Storage abstracted via backend API adapters</span>
                  </div>
                  <span className="text-blue-700 font-bold bg-blue-50 px-2.5 py-1 rounded-md border border-blue-200">
                    ✓ Ready
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* MODALS & DRAWERS */}
      {selectedAccount && isTimelineOpen && (
        <AccountTimelineDrawer
          account={selectedAccount}
          isOpen={isTimelineOpen}
          onClose={() => setIsTimelineOpen(false)}
          onOpenWatermarkCamera={() => setIsWatermarkOpen(true)}
          onOpenVoiceRecorder={() => setIsVoiceOpen(true)}
          onOpenFollowUp={() => setIsFollowUpOpen(true)}
          onOpenPTP={() => setIsPTPOpen(true)}
          onOpenRecovery={() => setIsRecoveryOpen(true)}
        />
      )}

      {selectedAccount && isWatermarkOpen && (
        <WatermarkCameraModal
          account={selectedAccount}
          isOpen={isWatermarkOpen}
          onClose={() => setIsWatermarkOpen(false)}
        />
      )}

      {selectedAccount && isVoiceOpen && (
        <VoiceNoteRecorderModal
          account={selectedAccount}
          isOpen={isVoiceOpen}
          onClose={() => setIsVoiceOpen(false)}
        />
      )}

      {selectedAccount && isFollowUpOpen && (
        <LogFollowUpModal
          account={selectedAccount}
          isOpen={isFollowUpOpen}
          onClose={() => setIsFollowUpOpen(false)}
        />
      )}

      {selectedAccount && isPTPOpen && (
        <CreatePTPModal
          account={selectedAccount}
          isOpen={isPTPOpen}
          onClose={() => setIsPTPOpen(false)}
        />
      )}

      {selectedAccount && isRecoveryOpen && (
        <RecordRecoveryModal
          account={selectedAccount}
          isOpen={isRecoveryOpen}
          onClose={() => setIsRecoveryOpen(false)}
        />
      )}

      {selectedAccount && isVisitOpen && (
        <FieldVisitModal
          account={selectedAccount}
          isOpen={isVisitOpen}
          onClose={() => setIsVisitOpen(false)}
          onOpenWatermarkCamera={() => setIsWatermarkOpen(true)}
        />
      )}

      {selectedAccount && isDocOpen && (
        <UploadDocumentModal
          account={selectedAccount}
          isOpen={isDocOpen}
          onClose={() => setIsDocOpen(false)}
        />
      )}

      {selectedAccount && isWhatsAppOpen && (
        <WhatsAppTriggerModal
          account={selectedAccount}
          isOpen={isWhatsAppOpen}
          onClose={() => setIsWhatsAppOpen(false)}
        />
      )}

      {selectedAccount && isAIOpen && (
        <AIRecoveryDrawer
          account={selectedAccount}
          isOpen={isAIOpen}
          onClose={() => setIsAIOpen(false)}
        />
      )}

      {/* Excel Allocation Modal */}
      <ExcelAllocationModal
        isOpen={isExcelModalOpen}
        onClose={() => setIsExcelModalOpen(false)}
      />

      {/* Customer Consolidated Single View Modal */}
      {selectedAccount && (
        <CustomerConsolidatedDetailModal
          account={selectedAccount}
          isOpen={isSingleCustomerModalOpen}
          onClose={() => setIsSingleCustomerModalOpen(false)}
          onOpenNoteModal={() => {
            setIsSingleCustomerModalOpen(false);
            setIsAddNoteModalOpen(true);
          }}
          onOpenRecoveryModal={() => {
            setIsSingleCustomerModalOpen(false);
            setIsRecoveryOpen(true);
          }}
          onOpenPTPModal={() => {
            setIsSingleCustomerModalOpen(false);
            setIsPTPOpen(true);
          }}
        />
      )}

      {/* Add Agent Note Modal */}
      {selectedAccount && (
        <AddAgentNoteModal
          account={selectedAccount}
          isOpen={isAddNoteModalOpen}
          onClose={() => setIsAddNoteModalOpen(false)}
        />
      )}

      {/* PTP Digest & Batch Sync Modal */}
      <PTPDigestModal
        isOpen={isPTPDigestOpen}
        onClose={() => setIsPTPDigestOpen(false)}
        onNavigateToAccountsWithFilter={(filter) => {
          setFilterStatus(filter);
          setActiveSection('accounts');
        }}
      />

      {/* CREATE NEW USER MODAL */}
      {/* Credentials, shown once after a user is created. */}
      {createdCredentials && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-md shadow-2xl overflow-y-auto max-h-[92vh]">
            <div className="px-6 py-4 bg-emerald-600 text-white">
              <h3 className="text-sm font-bold">User created</h3>
              <p className="text-[11px] text-emerald-50">
                Write these down or send them now — the password cannot be
                shown again, only reset.
              </p>
            </div>
            <div className="p-6 space-y-3 text-xs">
              <div>
                <span className="block text-slate-500 font-semibold">Name</span>
                <span className="font-bold text-slate-900">
                  {createdCredentials.name}
                </span>
              </div>
              <div>
                <span className="block text-slate-500 font-semibold">Login ID</span>
                <span className="font-mono font-black text-blue-700 text-sm">
                  {createdCredentials.loginId}
                </span>
              </div>
              <div>
                <span className="block text-slate-500 font-semibold">Password</span>
                <span className="font-mono font-black text-slate-900 text-sm">
                  {createdCredentials.password}
                </span>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    const text = `Login ID: ${createdCredentials.loginId}\nPassword: ${createdCredentials.password}`;
                    try {
                      void navigator.clipboard.writeText(text);
                    } catch {
                      /* clipboard may be unavailable; the values are on screen */
                    }
                  }}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition cursor-pointer"
                >
                  Copy
                </button>
                <button
                  type="button"
                  onClick={() => setCreatedCredentials(null)}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition cursor-pointer"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isCreateUserOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-y-auto max-h-[92vh] border border-slate-200 animate-in fade-in">
            <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                <h3 className="text-sm font-bold">Create New Portal &amp; App User</h3>
              </div>
              <button
                onClick={() => setIsCreateUserOpen(false)}
                className="text-slate-400 hover:text-white p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={handleCreateUserSubmit}
              className="p-6 space-y-4 text-xs max-h-[78vh] overflow-y-auto"
            >
              {/* The role is asked FIRST because it decides what the
                  hierarchy below must supply: an Administrator needs no
                  branch, a Branch Manager needs exactly one, an Agent needs at
                  least one plus a plan. Asking for it last meant the form
                  could not know which fields were required while they were
                  being filled in. */}
              <div>
                <label className="block text-slate-700 font-bold mb-1">Role *</label>
                <select
                  value={newUserRole}
                  onChange={(e) => {
                    const next = e.target.value as UserRole;
                    setNewUserRole(next);
                    setNewUserProblems([]);
                    // A role change changes what is required, so a selection
                    // made for the previous role is not carried over silently.
                    const req = requirementsFor(next);
                    if (req.branch === false) setNewUserBranches([]);
                    if (req.branch === 'one') {
                      setNewUserBranches((prev) => (prev.length > 1 ? prev.slice(0, 1) : prev));
                    }
                    if (next !== 'agent') setNewUserPlan(emptyAgentPlan);
                  }}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="agent">Recovery Agent (Field/Web)</option>
                  <option value="branch_manager">Branch Manager</option>
                  <option value="recovery_department">Recovery Department User</option>
                  <option value="admin">System Administrator</option>
                  <option value="coordinator">Coordinator</option>
                  <option value="management">Executive Management</option>
                </select>
              </div>

              {/* Step 1 -- Bank, then Zonal Office, then Branch, each list
                  filtered by the one above it, each with a "+ Create" beside
                  it so a missing item does not send you to another screen and
                  lose everything already typed here. */}
              <HierarchyPicker
                role={newUserRole}
                banks={newUserBanks}
                onBanksChange={(next) => {
                  setNewUserBanks(next);
                  setNewUserProblems([]);
                }}
                zone={newUserZonalOffice}
                onZoneChange={(next) => {
                  setNewUserZonalOffice(next);
                  setNewUserProblems([]);
                }}
                branches={newUserBranches}
                onBranchesChange={(next) => {
                  setNewUserBranches(next);
                  setNewUserProblems([]);
                }}
              />

              {newUserBanks.length > 0 && (
                <div>
                  <label className="block text-slate-700 font-bold mb-1">
                    Commission %
                    <span className="ml-1 font-medium text-slate-500">
                      (from the bank — you may change it)
                    </span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={newUserCommissionPct}
                    onChange={(e) => {
                      setCommissionPctTouched(true);
                      setNewUserCommissionPct(e.target.value);
                    }}
                    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 font-mono font-bold text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              )}

              {newUserRole === 'agent' && (
                <AgentPlanFields
                  value={newUserPlan}
                  onChange={(next) => {
                    setNewUserPlan(next);
                    setNewUserProblems([]);
                  }}
                />
              )}

              <span className="font-bold text-slate-900 block">Step 2 · User details</span>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Name / Agency Name *</label>
                  <input
                    type="text"
                    required
                    value={newUserName}
                    onChange={(e) => setNewUserName(e.target.value)}
                    placeholder="e.g. Anand Deshmukh or Apex Recovery Services"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1">User ID / Agent ID (Login ID) *</label>
                  <input
                    type="text"
                    required
                    value={newUserId}
                    onChange={(e) => setNewUserId(e.target.value)}
                    placeholder={newUserRole === 'agent' ? 'e.g. RA-0048 (or leave blank to auto-generate)' : 'e.g. admin_pune'}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 font-mono focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1">Login Password *</label>
                  <input
                    type="text"
                    required
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    placeholder="Password (e.g. 1234)"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 font-mono focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1">
                    Email Address <span className="font-normal text-slate-400">(optional — contact only)</span>
                  </label>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="Optional. Login uses the User ID above."
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1">Mobile Contact *</label>
                  <input
                    type="text"
                    required
                    value={newUserMobile}
                    onChange={(e) => setNewUserMobile(e.target.value)}
                    placeholder="+91 98000 00000"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 font-mono focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                {/* Bank and zone are chosen in Step 1 above; a second copy
                    here would let the two disagree. */}

                {/* The "Regional Office *" picker was here. Regional Office
                    is no longer a level in the hierarchy -- Bank -> Zonal
                    Office -> Branch -- and it was never anything but a text
                    field anyway: no record, no parent, nothing under it. It
                    is no longer asked for when a user is created. */}
              </div>

              {/* Everything that is missing, at once. This screen used to
                  accept a user with no bank, no zone and no branch -- they
                  signed in and saw nothing, and nobody knew why. */}
              {newUserProblems.length > 0 && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl space-y-1.5">
                  <div className="flex items-center gap-1.5 font-bold text-rose-800">
                    <AlertTriangle className="w-4 h-4" />
                    <span>
                      This user cannot be created yet —{' '}
                      {newUserProblems.length === 1
                        ? '1 thing is missing'
                        : `${newUserProblems.length} things are missing`}
                    </span>
                  </div>
                  <ul className="list-disc pl-5 space-y-0.5 text-rose-700">
                    {newUserProblems.map((p, i) => (
                      <li key={`nu-problem-${i}`}>{p}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    setIsCreateUserOpen(false);
                    setNewUserProblems([]);
                  }}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreatingUser}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-xl font-bold flex items-center gap-1.5 shadow-sm transition"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{isCreatingUser ? 'Creating…' : 'Create User'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {isEditUserOpen && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-y-auto max-h-[92vh] border border-slate-200 animate-in fade-in">
            <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-blue-400" />
                <h3 className="text-sm font-bold">Edit User Details: {editingUser.name}</h3>
              </div>
              <button
                onClick={() => setIsEditUserOpen(false)}
                className="text-slate-400 hover:text-white p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={handleEditUserSubmit}
              className="p-6 space-y-4 text-xs max-h-[78vh] overflow-y-auto"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Full Name</label>
                  <input
                    type="text"
                    required
                    value={editUserName}
                    onChange={(e) => setEditUserName(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1">Role</label>
                  <select
                    value={editUserRole}
                    onChange={(e) => setEditUserRole(e.target.value as UserRole)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="agent">Recovery Agent</option>
                    <option value="branch_manager">Branch Manager</option>
                    <option value="recovery_department">Recovery Department User</option>
                    <option value="admin">System Administrator</option>
                    <option value="coordinator">Coordinator</option>
                    <option value="management">Executive Management</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1">
                    Agent Code <span className="font-normal text-slate-400">(printed on records)</span>
                  </label>
                  <input
                    type="text"
                    value={editUserAgentId}
                    onChange={(e) => setEditUserAgentId(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 font-mono focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    This is not what they sign in with.
                  </p>
                </div>

                {/* THE SIGN-IN ID, which is a different thing entirely and
                    used to have nowhere to be changed. See changeUserLoginId
                    in the context for why editing it anywhere else breaks
                    sign-in. */}
                <div className="md:col-span-2 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                  <label className="block text-slate-700 font-bold mb-1">Sign-in ID</label>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      value={editLoginId}
                      onChange={(e) => setEditLoginId(e.target.value)}
                      placeholder="e.g. admin"
                      className="flex-1 min-w-[10rem] bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-900 font-mono focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <button
                      type="button"
                      disabled={
                        changingLoginId ||
                        !editLoginId.trim() ||
                        editLoginId.trim().toLowerCase() === actualSignInId.toLowerCase()
                      }
                      onClick={async () => {
                        if (!editingUser) return;
                        const next = editLoginId.trim();
                        if (
                          !window.confirm(
                            `Change the sign-in ID for ${editingUser.name} to "${next}"?\n\n` +
                              `They will have to sign in with "${next}" from now on. "${actualSignInId}" will stop working.`
                          )
                        )
                          return;
                        setChangingLoginId(true);
                        setLoginIdNote('');
                        try {
                          const res = await changeUserLoginId(editingUser.id, next);
                          setLoginIdNote(
                            res.success
                              ? `Done. They now sign in as "${res.loginId}".`
                              : res.error || 'It could not be changed.'
                          );
                          if (res.success && res.loginId) setEditLoginId(res.loginId);
                        } finally {
                          setChangingLoginId(false);
                        }
                      }}
                      className="px-3 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold transition cursor-pointer"
                    >
                      {changingLoginId ? 'Changing…' : 'Change sign-in ID'}
                    </button>
                  </div>

                  <p className="text-[11px] text-slate-500 mt-1.5">
                    Signing in currently works with{' '}
                    <strong className="font-mono">{actualSignInId || '—'}</strong>. Changing it
                    here updates the login itself — editing it in the database does not, and
                    leaves the two disagreeing.
                  </p>

                  {signInIdMismatch && (
                    <div className="mt-2 flex items-start gap-2 text-[11px] bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 text-amber-900">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>
                        This record says the ID is{' '}
                        <strong className="font-mono">{editingUser?.loginId || editingUser?.username}</strong>
                        , but sign-in still only accepts{' '}
                        <strong className="font-mono">{actualSignInId}</strong>. Press “Change
                        sign-in ID” to make them agree.
                      </span>
                    </div>
                  )}

                  {loginIdNote && (
                    <p className="text-[11px] font-bold text-slate-700 mt-1.5">{loginIdNote}</p>
                  )}
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1">Mobile Contact</label>
                  <input
                    type="text"
                    value={editUserMobile}
                    onChange={(e) => setEditUserMobile(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 font-mono focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1">Email</label>
                  <input
                    type="email"
                    value={editUserEmail}
                    onChange={(e) => setEditUserEmail(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1">Assigned Bank</label>
                  <select
                    value={editUserBank}
                    onChange={(e) => setEditUserBank(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="All Banks">All Banks (Multi-Bank Access)</option>
                    {allUniqueBanks.map((b, idx) => (
                      <option key={`edit-user-bank-${b || idx}`} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-slate-700 font-bold mb-1">
                    Branches{editUserBranches.length > 1 ? ` (${editUserBranches.length})` : ''}
                    <span className="ml-1 font-medium text-slate-500">
                      — tick every branch this user covers
                    </span>
                  </label>
                  <div className="max-h-32 overflow-y-auto bg-white border border-slate-200 rounded-xl p-2 space-y-1">
                    {allUniqueBranches.length === 0 ? (
                      <span className="text-slate-400">No branches yet.</span>
                    ) : (
                      allUniqueBranches.map((b, idx) => {
                        const on = editUserBranches.includes(b);
                        return (
                          <label
                            key={`edit-user-br-${b || idx}`}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={(e) =>
                                setEditUserBranches((prev) =>
                                  e.target.checked
                                    ? [...prev, b]
                                    : prev.filter((x) => x !== b)
                                )
                              }
                            />
                            <span className="text-slate-800 font-medium">{b}</span>
                            {on && editUserBranches[0] === b && (
                              <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-[9px] font-bold">
                                PRIMARY
                              </span>
                            )}
                          </label>
                        );
                      })
                    )}
                  </div>
                  {editUserBranches.length === 0 && (
                    <p className="mt-1 text-[11px] text-amber-700">
                      No branch ticked — this user will not see any branch's accounts.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1">Administrative Zone</label>
                  <input
                    type="text"
                    value={editUserZone}
                    onChange={(e) => setEditUserZone(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1">Administrative Zone</label>
                  <input
                    type="text"
                    value={editUserZone}
                    onChange={(e) => setEditUserZone(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                {editUserRole === 'agent' && (
                  <div>
                    <label className="block text-slate-700 font-bold mb-1">Monthly Target (₹)</label>
                    <AmountInput
                      value={editUserTarget}
                      onValueChange={setEditUserTarget}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 font-mono focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                )}
              </div>

              {/* Plan, editable after creation -- extend a demo, change dates,
                  or turn a demo user into a subscriber without losing their
                  Agent ID and everything allotted to it. */}
              {editUserRole === 'agent' && (
                <>
                  {editUserPlan.plan === 'demo' && (
                    <div className="flex items-center justify-between gap-3 p-3 bg-blue-50/70 border border-blue-100 rounded-xl">
                      <span className="text-[11px] text-blue-900 font-semibold">
                        Currently on Free Demo
                        {editUserPlan.demoEndDate ? ` until ${editUserPlan.demoEndDate}` : ''}.
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setEditUserPlan((prev) => ({
                            ...prev,
                            plan: 'subscription',
                            // The subscription starts where the demo ended, so
                            // there is no gap and no overlap.
                            subscriptionStartDate:
                              prev.subscriptionStartDate || prev.demoEndDate || isoDay(0),
                            subscriptionEndDate: prev.subscriptionEndDate || isoDay(365),
                            paymentDate: prev.paymentDate || isoDay(0),
                          }))
                        }
                        className="px-3 py-1.5 bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 rounded-lg font-bold text-[11px] transition shrink-0"
                      >
                        Convert to Subscription
                      </button>
                    </div>
                  )}

                  <AgentPlanFields value={editUserPlan} onChange={setEditUserPlan} />
                </>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsEditUserOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold flex items-center gap-1.5 shadow-sm transition"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Save Changes</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CHANGE / RESET USER PASSWORD MODAL */}
      {isChangePasswordOpen && passwordTargetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-y-auto max-h-[92vh] border border-slate-200 animate-in fade-in">
            <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FolderLock className="w-5 h-5 text-amber-400" />
                <h3 className="text-sm font-bold">Admin Password &amp; PIN Reset</h3>
              </div>
              <button
                onClick={() => setIsChangePasswordOpen(false)}
                className="text-slate-400 hover:text-white p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleChangePasswordSubmit} className="p-6 space-y-4 text-xs">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                <p className="text-slate-600 font-medium">Resetting credentials for:</p>
                <p className="text-slate-900 font-bold text-sm">{passwordTargetUser.name}</p>
                <p className="text-slate-500 font-mono text-[11px]">
                  User ID: {passwordTargetUser.agentId || passwordTargetUser.id} | Role: {passwordTargetUser.role.toUpperCase()}
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-slate-700 font-bold mb-1">New Login Password *</label>
                  <input
                    type="text"
                    required
                    value={newPasswordInput}
                    onChange={(e) => setNewPasswordInput(e.target.value)}
                    placeholder="Enter new password (e.g. agent2026 or 1234)"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 font-mono text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">User can use this password to log in immediately across Web and Android.</p>
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1">4-Digit Quick PIN (Optional)</label>
                  <input
                    type="text"
                    maxLength={6}
                    value={newPinInput}
                    onChange={(e) => setNewPinInput(e.target.value)}
                    placeholder="1234"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 font-mono text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsChangePasswordOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold flex items-center gap-1.5 shadow-sm transition"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Update Password</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE USER CONFIRMATION MODAL */}
      {isDeleteUserOpen && userToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-y-auto max-h-[92vh] border border-slate-200 animate-in fade-in">
            <div className="px-6 py-4 bg-rose-600 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-white" />
                <h3 className="text-sm font-bold">Confirm Delete User</h3>
              </div>
              <button
                onClick={() => setIsDeleteUserOpen(false)}
                className="text-rose-200 hover:text-white p-1 rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="p-3.5 bg-rose-50 rounded-xl border border-rose-200 space-y-1.5">
                <p className="text-slate-600 font-medium">You are about to delete user:</p>
                <p className="text-slate-900 font-bold text-sm">{userToDelete.name}</p>
                <p className="text-slate-500 font-mono text-[11px]">
                  User ID: {userToDelete.agentId || userToDelete.id} | Role: {userToDelete.role.toUpperCase()}
                </p>
              </div>

              <div className="p-3.5 bg-amber-50 rounded-xl border border-amber-200 text-amber-900 space-y-1.5 text-[11px] leading-relaxed">
                <div className="font-bold flex items-center gap-1.5 text-amber-800">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                  <span>Automatic Customer Reassignment &amp; Record Preservation</span>
                </div>
                <ul className="list-disc pl-4 space-y-1 text-slate-700">
                  <li>
                    All customer accounts assigned to <strong>{userToDelete.name}</strong> will be automatically reassigned to the <strong>Management Queue</strong>.
                  </li>
                  <li>
                    All historical remarks, notes, PTP commitments, recovery payments, and field visit logs updated by this agent will remain <strong>100% preserved</strong> and intact.
                  </li>
                  <li>
                    Active login sessions for this user will be revoked immediately.
                  </li>
                </ul>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsDeleteUserOpen(false)}
                  disabled={isDeletingUser}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDeleteUser}
                  disabled={isDeletingUser}
                  className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold flex items-center gap-1.5 shadow-sm transition cursor-pointer disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>{isDeletingUser ? 'Deleting...' : 'Confirm & Delete User'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CUSTOMER ACCOUNT CONFIRMATION MODAL */}
      {isDeleteAccountOpen && accountToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-y-auto max-h-[92vh] border border-slate-200 animate-in fade-in">
            <div className="px-6 py-4 bg-rose-600 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-white" />
                <h3 className="text-sm font-bold">Confirm Delete Customer Account</h3>
              </div>
              <button
                onClick={() => setIsDeleteAccountOpen(false)}
                className="text-rose-200 hover:text-white p-1 rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="p-3.5 bg-rose-50 rounded-xl border border-rose-200 space-y-1.5">
                <p className="text-slate-600 font-medium">You are about to delete customer account:</p>
                <p className="text-slate-900 font-bold text-sm">{accountToDelete.customerName}</p>
                <p className="text-slate-500 font-mono text-[11px]">
                  Account ID: {accountToDelete.accountId} | Loan No: {accountToDelete.loanNumber} | Overdue: {formatINR(accountToDelete.overdueAmount)}
                </p>
                <p className="text-slate-500 text-[11px]">
                  Assigned Agent: {accountToDelete.assignedAgentName} ({accountToDelete.assignedAgentId})
                </p>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-slate-600 text-[11px] leading-relaxed">
                <p>
                  This will remove the customer account record from the active database. If this customer has multiple linked loan accounts, only this specific account ({accountToDelete.accountId}) will be removed.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsDeleteAccountOpen(false)}
                  disabled={isDeletingAccount}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDeleteAccount}
                  disabled={isDeletingAccount}
                  className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold flex items-center gap-1.5 shadow-sm transition cursor-pointer disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>{isDeletingAccount ? 'Deleting...' : 'Delete Customer Account'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isCategoryModalOpen && <AccountCategoryModal onClose={() => setIsCategoryModalOpen(false)} />}

      {/* DELETE ALL ACCOUNTS FROM STORAGE CONFIRMATION MODAL */}
      {isDeleteAllAccountsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-y-auto max-h-[92vh] border border-slate-200 animate-in fade-in">
            <div className="px-6 py-4 bg-rose-700 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-white" />
                <h3 className="text-sm font-bold">Delete All Permanent Account Data</h3>
              </div>
              <button
                onClick={() => setIsDeleteAllAccountsModalOpen(false)}
                className="text-rose-200 hover:text-white p-1 rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="p-3.5 bg-rose-50 rounded-xl border border-rose-200 space-y-1.5">
                <p className="text-rose-900 font-bold text-sm">Permanent Storage Wipe (Accounts Only)</p>
                <p className="text-slate-600 text-xs leading-relaxed">
                  You are about to permanently delete <b>all {accounts.length} customer accounts</b> from local and permanent storage.
                </p>
                <div className="p-2.5 bg-white/80 rounded-lg border border-rose-100 text-[11px] text-slate-700 space-y-1">
                  <p className="text-emerald-700 font-bold">✓ Safe: Users, login credentials, branches, and zones will NOT be deleted.</p>
                  <p className="text-rose-700 font-bold">✗ Wiped: All customer account records, balances, and loan details will be cleared to 0.</p>
                </div>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-slate-600 text-[11px] leading-relaxed">
                <p>
                  After deletion, the account database will be completely clear (0 accounts). You can upload a new Excel file or synchronize fresh records with Cloud Firestore at any time.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsDeleteAllAccountsModalOpen(false)}
                  disabled={isDeletingAllAccounts}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDeleteAllAccounts}
                  disabled={isDeletingAllAccounts}
                  className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold flex items-center gap-1.5 shadow-sm transition cursor-pointer disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>{isDeletingAllAccounts ? 'Deleting All Accounts...' : 'Confirm & Delete All Accounts'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* BULK DELETE ACCOUNTS MODAL (BY AGENT, BRANCH, ALL, SELECTED) */}
      <BulkDeleteAccountsModal
        isOpen={isBulkDeleteModalOpen}
        onClose={() => setIsBulkDeleteModalOpen(false)}
        initialScope={bulkDeleteInitialScope}
        preselectedAgentId={bulkDeletePreselectedAgent}
        preselectedBranchName={bulkDeletePreselectedBranch}
        selectedAccountIds={selectedAccountIds}
        onSuccess={() => {
          setSelectedAccountIds([]);
        }}
      />

      {/* REVERSAL CONFIRMATION MODAL */}
      {isReversalModalOpen && reversalTargetRecovery && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-y-auto max-h-[92vh] border border-slate-200 animate-in fade-in">
            <div className="px-6 py-4 bg-rose-600 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-white" />
                <h3 className="text-sm font-bold">Reverse Recovery Entry</h3>
              </div>
              <button
                onClick={() => setIsReversalModalOpen(false)}
                className="text-rose-200 hover:text-white p-1 rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="p-3.5 bg-rose-50 rounded-xl border border-rose-200 space-y-1.5">
                <p className="text-slate-600 font-medium">Reversing receipt:</p>
                <p className="text-slate-900 font-bold text-sm font-mono">{reversalTargetRecovery.receiptNumber}</p>
                <p className="text-slate-700 font-semibold">
                  {reversalTargetRecovery.customerName} ({reversalTargetRecovery.accountId})
                </p>
                <p className="text-emerald-700 font-mono font-bold text-base">
                  Amount: {formatINR(reversalTargetRecovery.amount)}
                </p>
                <p className="text-slate-500 text-[11px]">
                  Payment Mode: {reversalTargetRecovery.paymentMode} | Date: {reversalTargetRecovery.recoveryDate}
                </p>
              </div>

              <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-amber-900 text-[11px] leading-relaxed">
                <p className="font-semibold text-amber-950 mb-1">Impact of Reversal:</p>
                <p>
                  Reversing will mark this receipt as reversed, restore ₹{reversalTargetRecovery.amount.toLocaleString('en-IN')} back to the customer's overdue/outstanding balance, cancel the commission earned, and record an audit log entry.
                </p>
              </div>

              <div>
                <label className="block text-slate-700 font-bold mb-1">Reason for Reversal *</label>
                <input
                  type="text"
                  required
                  value={reversalReasonInput}
                  onChange={(e) => setReversalReasonInput(e.target.value)}
                  placeholder="e.g. Cheque bounced / Customer paid wrong amount / Duplicate entry"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 text-xs focus:bg-white focus:ring-2 focus:ring-rose-500 focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsReversalModalOpen(false)}
                  disabled={isProcessingReversal}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmReversal}
                  disabled={isProcessingReversal}
                  className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold flex items-center gap-1.5 shadow-sm transition cursor-pointer disabled:opacity-50"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>{isProcessingReversal ? 'Reversing...' : 'Confirm Reversal'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CLOSE / DELETE ACCOUNT MODAL */}
      {accountToClose && (
        <CloseAccountModal
          account={accountToClose}
          isOpen={isCloseAccountModalOpen}
          onClose={() => {
            setIsCloseAccountModalOpen(false);
            setAccountToClose(null);
          }}
          onSuccess={() => {
            setIsCloseAccountModalOpen(false);
            setAccountToClose(null);
            setUserAdminFeedback('Account closure / deletion successfully updated.');
            setTimeout(() => setUserAdminFeedback(null), 5000);
          }}
        />
      )}

      {/* OTS SETTINGS MODAL (Bank-wise Last Dates & Scheme Slabs - Admin Only) */}
      <OTSSettingsModal
        isOpen={isOTSSettingsOpen}
        onClose={() => setIsOTSSettingsOpen(false)}
      />

      {/* BACKEND DATABASE & STORAGE MANAGER MODAL */}
      <BackendStorageManagerModal
        isOpen={isBackendStorageModalOpen}
        onClose={() => setIsBackendStorageModalOpen(false)}
      />
    </div>
  );
};
